/** Cart-side custom elements for the YISE cart renderer. */
import { Component, defineElement } from '@theme/component';
import { cart, checkoutTriggerFromEvent } from '@theme/cart';
import {
  formatItemCount,
  formatMoney,
  linesShareIdentity,
  optimisticOriginalPrice,
  presentmentThresholdOf,
  resolveDiscountPricing,
} from '@theme/cart-math';
import {
  isGiftLine,
  milestoneStageProgress,
  normalizeAjaxCart,
  remainingCentsFor,
  subtotalCentsFor,
} from '@theme/cart-gwp-rules';
import { morph } from '@theme/morph';

/** Section id the drawer registers with the engine when its markup names none. */
const CART_SECTION_ID = 'cart-drawer';

const DEFAULT_MONEY_FORMAT = '${{amount}}';

/** Env island written by `snippets/cart-engine.liquid`; `moneyFormat` is `shop.money_format`. */
let env = { moneyFormat: DEFAULT_MONEY_FORMAT };

/** Per-item quantity cap, read live from the cart drawer's root element (set by section settings). */
function cartMaxItemQuantity() {
  const el = document.querySelector('[data-max-item-quantity]');
  const v = el ? Number(el.dataset.maxItemQuantity) : 0;
  return Number.isFinite(v) ? v : 0;
}

/** Whether gift-card lines are kept out of a threshold subtotal. */
let giftCardsExcludedCache = true;
let giftCardsExcludedSource = null;
function giftCardsExcluded() {
  const raw = document.querySelector('[data-gwp-config]')?.textContent ?? '';
  if (raw !== giftCardsExcludedSource) {
    giftCardsExcludedSource = raw;
    let parsed = null;
    try {
      parsed = JSON.parse(raw || 'null');
    } catch {
      parsed = null;
    }
    giftCardsExcludedCache = parsed?.excludeGiftCards !== false;
  }
  return giftCardsExcludedCache;
}

/** The pluralized item-count samples the subtotal parenthetical is written from. */
let subtotalCountSamplesCache = [];
let subtotalCountSamplesSource = null;
function subtotalCountSamples(root) {
  const raw = root.querySelector('[data-subtotal-count-samples]')?.textContent ?? '';
  if (raw !== subtotalCountSamplesSource) {
    subtotalCountSamplesSource = raw;
    let parsed = null;
    try {
      parsed = JSON.parse(raw || '[]');
    } catch {
      parsed = null;
    }
    subtotalCountSamplesCache = Array.isArray(parsed) ? parsed : [];
  }
  return subtotalCountSamplesCache;
}

/** Serialize a product form the way Shopify would: id, quantity, properties[], selling_plan. */
export function itemFromForm(form) {
  const data = new FormData(form);
  const item = { id: Number(data.get('id')), quantity: Number(data.get('quantity') || 1) };
  const sellingPlan = data.get('selling_plan');
  if (sellingPlan) item.selling_plan = Number(sellingPlan);
  const properties = {};
  for (const [name, value] of data.entries()) {
    const match = name.match(/^properties\[(.+)\]$/);
    if (match && value !== '') properties[match[1]] = value;
  }
  if (Object.keys(properties).length) item.properties = properties;
  return item;
}

/** Read the trusted, Liquid-rendered display data attached to one add surface.
 *  This payload stays client-only and gives a new projected line enough real
 *  content to render before Shopify returns its authoritative line markup. */
export function optimisticLinePresentation(source, item = {}) {
  const title = source?.dataset?.cartLineTitle?.trim();
  if (!title) return null;
  const explicitVariantTitle = source.dataset.cartLineVariantTitle?.trim()
    || source.dataset.cartUpsellVariantTitle?.trim()
    || '';
  const legacyVariantTitle = source.dataset.cartLineSubtitle?.trim() || '';
  const variantTitle = explicitVariantTitle || legacyVariantTitle;
  const url = source.dataset.productUrl?.trim()
    || source.closest?.('[data-product-url]')?.dataset.productUrl?.trim()
    || '';
  const mediaTemplate = source.querySelector?.('template[data-cart-line-media]');
  const image = mediaTemplate?.content?.querySelector('img');
  const sellingPlanSelect = source.querySelector?.('select[name="selling_plan"]:not(:disabled)');
  const sellingPlanOption = sellingPlanSelect?.selectedOptions?.[0];
  /* YISE renders purchase options as radios plus a hidden `selling_plan` input rather than a
     <select>, so when there is no option element the selected plan's price and cart label ride
     on that input. An empty value is one-time, which must keep the variant's own price. */
  const sellingPlanInput = sellingPlanSelect
    ? null
    : source.querySelector?.('input[name="selling_plan"]:not(:disabled)');
  const planSource = sellingPlanOption
    || (sellingPlanInput?.value?.trim() ? sellingPlanInput : null);
  const pricingState = optimisticPricingState(source, planSource, item);
  const finalPrice = finiteNumber(pricingState?.price)
    ?? finiteNumber(planSource?.dataset?.planPrice)
    ?? finiteNumber(source.dataset.variantPrice)
    ?? finiteNumber(item.price);
  const planCompareAtPrice = finiteNumber(planSource?.dataset?.planCompareAtPrice);
  const originalPrice = optimisticOriginalPrice({
    tierCompareAtPrice: pricingState?.compareAtPrice,
    planCompareAtPrice,
    variantPrice: source.dataset.variantPrice,
    itemPrice: item.price,
    finalPrice,
    hasSellingPlan: Boolean(item.selling_plan),
  });
  const imageAttributes = {};
  for (const name of [
    'src',
    'srcset',
    'sizes',
    'width',
    'height',
    'alt',
    'class',
    'loading',
    'decoding',
    'fetchpriority',
  ]) {
    if (image?.hasAttribute(name)) imageAttributes[name] = image.getAttribute(name);
  }
  return {
    title,
    url,
    subtitle: explicitVariantTitle ? legacyVariantTitle : '',
    variantTitle: variantTitle === 'Default Title' ? '' : variantTitle,
    sellingPlanName: planSource?.dataset?.planCartLabel?.trim()
      || sellingPlanOption?.textContent?.trim()
      || '',
    sellingPlanSaveLabel: sellingPlanOption?.dataset?.planSaveLabel?.trim() || '',
    finalPrice,
    originalPrice,
    image: Object.keys(imageAttributes).length ? imageAttributes : null,
  };
}

/** Rewrite an add button's visible label without disturbing the icons beside it. */
export function setAddButtonLabel(button, label) {
  if (!label || button.textContent.trim() === label) return;
  const labelElement = button.querySelector('[data-button-text]');
  if (labelElement) {
    labelElement.textContent = label;
    return;
  }
  for (const node of [...button.childNodes]) {
    if (node.nodeType === Node.TEXT_NODE) node.remove();
  }
  button.insertBefore(document.createTextNode(label), button.firstChild);
}

/** How many more units of `variantId` may be added before hitting the per-item cap. Gift lines are exempt. */
export function remainingAllowance(cartState, variantId, maxItemQuantity) {
  if (!maxItemQuantity || maxItemQuantity < 1) return Infinity;
  const inCart = cartState.items
    .filter((l) => Number(l.id) === Number(variantId) && !isGiftLine(l))
    .reduce((sum, l) => sum + l.quantity, 0);
  return Math.max(0, maxItemQuantity - inCart);
}

export function cartVisuallyHasItems(cartState, emptyTransitionRequested = false) {
  return !emptyTransitionRequested && Number(cartState?.item_count) > 0;
}

export function milestoneStageLayout(rows, thresholdOf, subtotalOf) {
  const ordered = rows
    .map((row, index) => ({ row, index, thresholdCents: thresholdOf(row) }))
    .sort((left, right) => (
      (left.thresholdCents > 0 ? 0 : 1) - (right.thresholdCents > 0 ? 0 : 1)
      || left.thresholdCents - right.thresholdCents
      || left.index - right.index
    ));

  let floorCents = 0;
  return ordered.map(({ row, thresholdCents }, position) => {
    const subtotalCents = subtotalOf(row);
    const { spanCents, progress } = milestoneStageProgress(
      thresholdCents,
      floorCents,
      subtotalCents,
    );
    if (thresholdCents > 0) floorCents = thresholdCents;
    return { row, position, thresholdCents, spanCents, progress, flexGrow: 1 };
  });
}

function maxQuantityMessage(button) {
  return (button?.dataset.textMax || '').replace('[max]', String(cartMaxItemQuantity()));
}

function finiteNumber(value) {
  if (value === '' || value === null || typeof value === 'undefined') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function jsonValue(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function cartSubtotalFromValue(value) {
  const detail = value?.detail || value || {};
  const explicitSubtotal = finiteNumber(detail.cartSubtotal);
  if (explicitSubtotal !== null) return explicitSubtotal;

  const cartValue = detail.cart || detail;
  if (Array.isArray(cartValue.items)) {
    return cartValue.items.reduce((subtotal, item) => {
      const originalLinePrice = finiteNumber(item.original_line_price);
      return subtotal + (originalLinePrice || 0);
    }, 0);
  }

  const originalTotalPrice = finiteNumber(cartValue.original_total_price);
  if (originalTotalPrice !== null) return originalTotalPrice;

  return finiteNumber(cartValue.originalTotalPrice);
}

function optimisticPricingState(source, sellingPlanOption, item) {
  const optionPricing = jsonValue(sellingPlanOption?.dataset?.planDiscountPricing);
  const discountPrice = source.querySelector?.('[data-variant-discount-price]');
  const pricing = optionPricing
    || jsonValue(source.dataset.cartLineDiscountPricing)
    || discountPrice?.overridePricing
    || discountPrice?.pricing;
  if (!pricing) return null;

  const now = Math.floor(Date.now() / 1000);
  const currentSubtotal = cartSubtotalFromValue(cart?.get()?.cart) || 0;
  const current = resolveDiscountPricing(pricing, currentSubtotal, now) || {};
  const originalUnitPrice = optimisticOriginalPrice({
    tierCompareAtPrice: current.compareAtPrice,
    variantPrice: source.dataset.variantPrice,
    itemPrice: item.price,
    finalPrice: current.price,
    hasSellingPlan: Boolean(item.selling_plan),
  }) ?? 0;
  const quantity = Math.max(1, finiteNumber(item.quantity) || 1);
  const projectedSubtotal = currentSubtotal + (originalUnitPrice * quantity);
  return resolveDiscountPricing(pricing, projectedSubtotal, now);
}

/** Beat the drawer waits after an add before sliding in. */
const CART_ADD_OPEN_DELAY_MS = 500;

/** Attempts the deferred drawer content gets before its frame offers the cart page instead. */
const CART_HYDRATION_MAX_RETRIES = 3;
const CART_HYDRATION_RETRY_MS = 700;
const CART_HYDRATION_LOAD_FALLBACK_MS = 3000;

/** Reduced motion is read lazily so import order relative to `frame.js` cannot pin a stale answer. */
const reducedMotion = {
  get matches() {
    if (typeof window === 'undefined') return false;
    const query = window.YiseFrame?.motionQuery
      ?? (typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null);
    return Boolean(query?.matches);
  },
};

function imageSourceKey(image) {
  const source = image?.getAttribute?.('src');
  if (!source) return '';
  try {
    const url = new URL(source, document.baseURI);
    return `${url.pathname}${url.search}`;
  } catch {
    return source;
  }
}

/**
 * Hides a row for an optimistic removal and reports the state change.
 *
 * The 'collapsed' report MUST stay in a microtask: `startRemoval` registers the
 * removal record after calling this, and a synchronous report would reach
 * `finishRemovalWhenReady` before that record exists, leaving the row attached.
 */
function collapseItem(item, onStateChange) {
  if (!item) {
    onStateChange?.('collapsed');
    return { restore() {}, dispose() {} };
  }

  let targetState = 'collapsed';
  let disposed = false;

  item.hidden = true;
  queueMicrotask(() => onStateChange?.('collapsed'));

  return {
    restore() {
      if (disposed || targetState === 'restored') return;
      targetState = 'restored';
      item.hidden = false;
      onStateChange?.('restored');
    },
    dispose() {
      disposed = true;
    },
  };
}

/**
 * Regions the renderer, `assets/cart-gwp.js` and the drawer's own inline modules
 * reveal and retire with the `hidden` property. The settle morph has to carry
 * that visibility across, or a region hidden client-side reappears on settle.
 */
/**
 * The last progress each milestone bar painted, so a bar the settle morph replaced can
 * be started from the width the old one was showing instead of snapping to the new one.
 * Module scope, because the component itself does not survive every morph.
 */
const milestoneProgress = new Map();

/** A milestone row's identity across re-renders. */
function milestoneRowKey(row) {
  return row.dataset.ruleHandle
    || `${row.dataset.ruleKind || 'tier'}:${row.dataset.thresholdCents || '0'}`;
}

/** A milestone row's place in the rendered sequence, which its `order` decides, not the DOM. */
function milestoneOrder(row) {
  const order = Number(row.style.order);
  return Number.isFinite(order) ? order : 0;
}

/** One fill's duration, read from the stylesheet so the stagger cannot drift from it. */
function milestoneFillMs(bar) {
  const seconds = Number.parseFloat(getComputedStyle(bar).transitionDuration);
  return Number.isFinite(seconds) ? seconds * 1000 : 0;
}

/** A milestone bar's target percent, from an explicit width or the section's own variable. */
function milestoneBarPercent(row, bar) {
  const explicit = /^\s*([\d.]+)%\s*$/.exec(bar.style.width || '');
  if (explicit) return Number(explicit[1]);
  const declared = /([\d.]+)%/.exec(row.style.getPropertyValue('--cart-drawer-progress') || '');
  return declared ? Number(declared[1]) : null;
}

const CART_CLIENT_REGION_SELECTOR = [
  '[data-gwp-milestone]',
  '[data-shipping-status]',
  'cart-samples-picker',
  '[data-samples-tier-message]',
  '[data-cart-tier-progress]',
  '[data-cart-rewards-auth]',
  '[data-cart-rewards-tooltip]',
].join(',');

/** Focus an element only once it is connected and actually rendered. */
function focusWhenVisible(element, options) {
  window.requestAnimationFrame(() => {
    if (element?.isConnected && element.getClientRects().length > 0) element.focus(options);
  });
}

/** Keep hidden drawer media cheap, then opt visible cart lines out of lazy loading. */
export function eagerLoadCartItemImages(root) {
  for (const image of root?.querySelectorAll?.('[data-cart-item] img[loading="lazy"]') || []) {
    image.loading = 'eager';
  }
}

/**
 * Live-region announce: clear -> rAF -> set, so repeat text re-announces.
 */
function announce(region, message, isError = false) {
  if (!region || !message) return;
  region.setAttribute('role', isError ? 'alert' : 'status');
  region.setAttribute('aria-live', isError ? 'assertive' : 'polite');
  region.textContent = '';
  window.requestAnimationFrame(() => { region.textContent = message; });
}

function cartLineTitle(row) {
  return row?.querySelector('.yise-cart__flyout-item-product-title-link')?.textContent?.trim()
    || row?.querySelector('.yise-cart__flyout-item-product-title')?.textContent?.trim()
    || 'Item';
}

function isNativeDialog(panel) {
  return typeof HTMLDialogElement !== 'undefined' && panel instanceof HTMLDialogElement;
}

/**
 * Open state for both panel shapes: the native `<dialog>` the contract prefers,
 * and the `div[role="dialog"]` YISE ships today.
 */
function panelIsOpen(panel) {
  if (!panel) return false;
  if (isNativeDialog(panel)) return panel.open;
  return panel.dataset.cartPanelOpen === 'true';
}

/**
 * The status region assistive tech can actually reach right now.
 *
 * While the drawer is open its panel is `aria-modal`, which prunes every sibling
 * from the accessibility tree, so the region inside the panel is the only one that
 * can be announced. While it is closed the panel is `inert`, so the outer one is.
 */
function cartLiveRegion(context) {
  const drawer = context?.closest?.('cart-drawer')
    || (context?.matches?.('cart-drawer') ? context : null)
    || document.querySelector('cart-drawer');
  if (!drawer) return null;
  const inDialog = drawer.querySelector('[data-cart-live-dialog]');
  if (inDialog && panelIsOpen(drawer.querySelector('#mini-cart'))) return inDialog;
  return drawer.querySelector('[data-cart-live]') || inDialog;
}

/** Manual Tab focus trap for the drawer panel (queried fresh so morphs are ok). */
function trapTab(panel, event) {
  if (!panel || event.key !== 'Tab') return;
  const focusable = [];
  const visit = (element) => {
    if (element.hidden || element.inert || element.getAttribute('aria-hidden') === 'true') return;
    if (element.tabIndex >= 0 && !element.matches(':disabled')
      && element.getClientRects().length > 0
      && getComputedStyle(element).visibility === 'visible') focusable.push(element);
    const children = element.shadowRoot
      ? element.shadowRoot.children
      : element instanceof HTMLSlotElement
        ? (element.assignedElements({ flatten: true }).length ? element.assignedElements({ flatten: true }) : element.children)
        : element.children;
    for (const child of children) visit(child);
  };
  visit(panel);
  const active = event.composedPath()[0] || document.activeElement;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!first || !last) return;
  if (event.shiftKey && active === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && active === last) {
    event.preventDefault();
    first.focus();
  }
}

/** Geometric hit-test — the panel fills a corner of the modal, so a point on the
 *  dialog element outside the panel box is a point on the backdrop. */
function isOutsidePanel(panel, event) {
  const bounds = panel.getBoundingClientRect();
  return (
    event.clientX < bounds.left ||
    event.clientX > bounds.right ||
    event.clientY < bounds.top ||
    event.clientY > bounds.bottom
  );
}

/** A click on a native dialog's backdrop is a dismiss. */
function isBackdropClick(panel, event) {
  return isNativeDialog(panel) && event.target === panel && isOutsidePanel(panel, event);
}

/**
 * Supports the drawer hook and the launcher markup in `sections/header-new.liquid`.
 */
const CART_TRIGGER_SELECTOR = '[data-cart-drawer-trigger], .js-toggle-cart[aria-controls="mini-cart"]';
const CART_TRIGGER_COUNT_SELECTOR = '[data-cart-drawer-trigger-count], .js-cart-count';

function cartTriggers() {
  return document.querySelectorAll(CART_TRIGGER_SELECTOR);
}

function updateTriggerCounts(count) {
  for (const trigger of cartTriggers()) {
    for (const el of trigger.querySelectorAll(CART_TRIGGER_COUNT_SELECTOR)) {
      if (el.textContent.trim() !== String(count)) el.textContent = count;
    }
    const cartLabel = trigger.dataset.cartDrawerLabel;
    const countLabel = trigger.dataset.cartDrawerCountLabel;
    if (cartLabel || countLabel) {
      trigger.setAttribute('aria-label', `${cartLabel || ''}, ${countLabel || ''}: ${count}`);
    }
  }
}

/** Open the cart drawer on the add beat, falling back to the theme's own action. */
function openCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (drawer && typeof drawer.openForAdd === 'function') {
    drawer.openForAdd();
    return;
  }
  window.Shopify?.actions?.openCart?.();
}

function cancelCartDrawerOpen(trigger) {
  document.getElementById('cart-drawer')?.cancelAddOpen?.(trigger);
}

export function rememberCartAddTrigger(trigger) {
  document.getElementById('cart-drawer')?.rememberAddTrigger?.(trigger);
}

/** A swallowed control reads as aria-disabled plus a cursor, never `disabled`, so it keeps focus. */
function setControlBusy(control, busy, opacity) {
  if (!control || control.dataset.disabledByVariant === 'true') return;
  control.setAttribute('aria-disabled', busy ? 'true' : 'false');
  if (busy) {
    control.style.cursor = 'not-allowed';
    if (opacity != null) control.style.opacity = String(opacity);
  } else {
    control.style.removeProperty('cursor');
    if (opacity != null) control.style.removeProperty('opacity');
  }
}

const ENABLED = typeof document !== 'undefined'
  && typeof window !== 'undefined';

/* Nothing below this line runs, defines a custom element, or attaches a
   listener outside a browser document, so the node test suite can import it. */
if (ENABLED) {
  const envEl = document.querySelector('[data-yise-cart-env]');
  let parsedEnv = null;
  try {
    parsedEnv = envEl ? JSON.parse(envEl.textContent) : null;
  } catch {
    parsedEnv = null;
  }
  env = { moneyFormat: DEFAULT_MONEY_FORMAT, ...(parsedEnv || {}) };
  if (!env.moneyFormat) env.moneyFormat = DEFAULT_MONEY_FORMAT;

  const money = (cents) => formatMoney(cents, env.moneyFormat);

  class VariantDiscountPrice extends Component {
    ready() {
      this.pricing = this.pricing || this.pricingFromDataset();
      if (typeof this.overridePricing === 'undefined') this.overridePricing = null;
      if (finiteNumber(this.cartSubtotal) === null) {
        this.cartSubtotal = finiteNumber(this.dataset.cartSubtotal) || 0;
      }

      for (const eventName of [
        'cart:refreshed',
        'yise:cart:updated',
        'shopify:cart:lines-update',
        'shopify:cart:discount-update',
      ]) {
        this.listen(document, eventName, (event) => this.handleCartUpdate(event));
      }
      this.listen(document, 'visibilitychange', () => {
        if (document.visibilityState !== 'hidden') this.refresh();
      });
      this.listen(window, 'pageshow', () => this.refresh());

      this.refresh();
    }

    teardown() {
      window.clearTimeout(this.scheduleTimer);
    }

    pricingFromDataset() {
      return {
        temporarySaleEnabled: this.dataset.temporarySaleEnabled === 'true',
        saleMode: this.dataset.saleMode || 'scheduled',
        saleStart: finiteNumber(this.dataset.saleStart),
        saleEnd: finiteNumber(this.dataset.saleEnd),
        tierTwoThreshold: finiteNumber(this.dataset.tierTwoThreshold),
        base: {
          price: finiteNumber(this.dataset.basePrice),
          compareAtPrice: finiteNumber(this.dataset.baseComparePrice),
          formattedPrice: this.dataset.baseFormattedPrice || '',
          formattedCompareAtPrice: this.dataset.baseFormattedComparePrice || '',
        },
        tierOne: this.dataset.tierOneFormattedPrice
          ? {
              price: finiteNumber(this.dataset.tierOnePrice),
              compareAtPrice: finiteNumber(this.dataset.tierOneComparePrice),
              formattedPrice: this.dataset.tierOneFormattedPrice,
              formattedCompareAtPrice: this.dataset.tierOneFormattedComparePrice || '',
            }
          : null,
        tierTwo: this.dataset.tierTwoFormattedPrice
          ? {
              price: finiteNumber(this.dataset.tierTwoPrice),
              compareAtPrice: finiteNumber(this.dataset.tierTwoComparePrice),
              formattedPrice: this.dataset.tierTwoFormattedPrice,
              formattedCompareAtPrice: this.dataset.tierTwoFormattedComparePrice || '',
            }
          : null,
      };
    }

    syncPricing(pricing) {
      this.pricing = pricing || {
        temporarySaleEnabled: false,
        saleMode: 'scheduled',
        saleStart: null,
        saleEnd: null,
        tierTwoThreshold: null,
        base: {
          price: null,
          compareAtPrice: null,
          formattedPrice: '',
          formattedCompareAtPrice: '',
        },
        tierOne: null,
        tierTwo: null,
      };
      this.overridePricing = null;
      this.refresh();
    }

    setPricingOverride(pricing) {
      this.overridePricing = pricing || null;
      this.refresh();
    }

    setPriceOverride(formattedPrice, formattedCompareAtPrice = '', price = null, compareAtPrice = null) {
      this.overridePricing = {
        temporarySaleEnabled: false,
        saleMode: 'scheduled',
        saleStart: null,
        saleEnd: null,
        tierTwoThreshold: null,
        base: {
          price: finiteNumber(price),
          compareAtPrice: finiteNumber(compareAtPrice),
          formattedPrice: formattedPrice || '',
          formattedCompareAtPrice: formattedCompareAtPrice || '',
        },
        tierOne: null,
        tierTwo: null,
      };
      this.refresh();
    }

    clearPriceOverride() {
      this.overridePricing = null;
      this.refresh();
    }

    handleCartUpdate(event) {
      this.refreshFromCartValue(event);

      const settlement = event.promise || event.detail?.promise;
      if (settlement && typeof settlement.then === 'function') {
        Promise.resolve(settlement)
          .then((value) => this.refreshFromCartValue(value))
          .catch(() => {});
      }
    }

    refreshFromCartValue(value) {
      const subtotal = cartSubtotalFromValue(value);
      if (subtotal === null) return;
      this.cartSubtotal = subtotal;
      this.refresh();
    }

    currentPricing(now) {
      return resolveDiscountPricing(
        this.overridePricing || this.pricing || {},
        this.cartSubtotal,
        now,
      );
    }

    refresh() {
      window.clearTimeout(this.scheduleTimer);

      const now = Math.floor(Date.now() / 1000);
      const current = this.currentPricing(now) || {};
      const currentPrice = finiteNumber(current.price);
      const currentCompareAtPrice = finiteNumber(current.compareAtPrice);
      const formattedPrice = current.formattedPrice || '';
      const formattedCompareAtPrice = current.formattedCompareAtPrice || '';

      if (currentPrice === null) delete this.dataset.currentPrice;
      else this.dataset.currentPrice = String(currentPrice);
      if (currentCompareAtPrice === null) delete this.dataset.currentCompareAtPrice;
      else this.dataset.currentCompareAtPrice = String(currentCompareAtPrice);

      const price = this.querySelector('[data-variant-discount-price-value]');
      if (price) price.textContent = formattedPrice;

      const compare = this.querySelector('[data-variant-discount-price-compare]');
      const compareValue = this.querySelector('[data-variant-discount-price-compare-value]');
      if (compareValue) compareValue.textContent = formattedCompareAtPrice;
      compare?.toggleAttribute('hidden', !formattedCompareAtPrice);

      this.querySelector('[data-variant-discount-price-label-regular]')
        ?.toggleAttribute('hidden', Boolean(formattedCompareAtPrice));
      this.querySelector('[data-variant-discount-price-label-sale]')
        ?.toggleAttribute('hidden', !formattedCompareAtPrice);

      this.scheduleNextBoundary(now);
    }

    scheduleNextBoundary(now) {
      const pricing = this.overridePricing || this.pricing;
      if (pricing?.saleMode === 'forced') return;

      const boundaries = [pricing?.saleStart, pricing?.saleEnd]
        .map(finiteNumber)
        .filter((boundary) => boundary !== null && boundary > now);
      if (!boundaries.length) return;

      const nextBoundary = Math.min(...boundaries);
      const delay = Math.min(Math.max((nextBoundary - now) * 1000 + 50, 50), 2147483000);
      this.scheduleTimer = window.setTimeout(() => this.refresh(), delay);
    }
  }

  class ProductFormComponent extends Component {
    async handleSubmit(event) {
      event.preventDefault();
      const form = this.querySelector('form');
      if (!(form instanceof HTMLFormElement) || this.dataset.busy) return;
      const item = itemFromForm(form);
      if (!item.id) return;
      const priceSource = form.dataset.variantPrice !== undefined ? form : this;
      const price = Number(priceSource.dataset.variantPrice);
      if (priceSource.dataset.variantPrice !== undefined && Number.isFinite(price) && price >= 0) {
        item.price = price;
      }
      const presentation = optimisticLinePresentation(form, item) || optimisticLinePresentation(this, item);
      if (presentation) item.__presentation = presentation;
      const button = event.submitter instanceof HTMLElement
        && event.submitter.getAttribute('name') === 'add'
        ? event.submitter
        : form.querySelector('[name="add"]');
      if (remainingAllowance(cart.get().cart, item.id, cartMaxItemQuantity()) < item.quantity) {
        const message = maxQuantityMessage(button);
        setButtonState(button, 'max', message);
        this.announce(message);
        scheduleButtonState(button, 'idle', 2000);
        return;
      }
      this.dataset.busy = 'true';
      setButtonState(button, 'adding');
      const promise = cart.add([item], { context: 'product', contextEl: this });
      rememberCartAddTrigger(button);
      openCartDrawer();
      try {
        const result = await promise;
        setButtonState(button, 'added');
        this.announce(addSuccessMessage(button, form, presentation, result?.cart));
        scheduleButtonState(button, 'idle', 2000);
      } catch (error) {
        cancelCartDrawerOpen(button);
        clearCartAnnouncement();
        setButtonState(button, 'error', addErrorMessage(button, form, presentation, error));
        scheduleButtonState(button, 'idle', 4000);
      } finally {
        delete this.dataset.busy;
      }
    }

    announce(message, isError = false) {
      announce(cartLiveRegion(this) || this.refs.liveRegion, message, isError);
    }
  }

  const buttonStateTimers = new WeakMap();

  function scheduleButtonState(button, state, delay) {
    if (!button) return;
    const current = buttonStateTimers.get(button);
    if (current) window.clearTimeout(current);
    const timer = window.setTimeout(() => {
      buttonStateTimers.delete(button);
      setButtonState(button, state);
    }, delay);
    buttonStateTimers.set(button, timer);
  }

  function replaceStatusTokens(template, values) {
    return Object.entries(values).reduce(
      (message, [token, value]) => message.replaceAll(`[${token}]`, String(value)),
      template,
    );
  }

  function addedProductTitle(button, form, presentation) {
    return presentation?.title
      || form?.dataset.cartLineTitle
      || button?.dataset.clickProduct
      || 'this item';
  }

  function addSuccessMessage(button, form, presentation, cartState) {
    const count = Number(cartState?.item_count || 0);
    const itemLabel = count === 1
      ? button?.dataset.textItemSingular || 'item'
      : button?.dataset.textItemPlural || 'items';
    const template = button?.dataset.textAddedStatus
      || 'Added [product] to bag. Your bag now has [count] [item_label], totaling [total].';
    return replaceStatusTokens(template, {
      product: addedProductTitle(button, form, presentation),
      count,
      item_label: itemLabel,
      total: formatMoney(Number(cartState?.total_price || 0), env.moneyFormat),
    });
  }

  function addErrorMessage(button, form, presentation, error) {
    const template = button?.dataset.textErrorStatus
      || 'Unable to add [product] to bag. [reason]. Try again.';
    const reason = String(error?.description || error?.message || 'The request could not be completed')
      .trim()
      .replace(/\.+$/, '');
    return replaceStatusTokens(template, {
      product: addedProductTitle(button, form, presentation),
      reason,
    });
  }

  function clearCartAnnouncement() {
    const drawer = document.getElementById('cart-drawer');
    for (const region of drawer?.querySelectorAll('[data-cart-live], [data-cart-live-dialog]') ?? []) {
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.textContent = '';
    }
  }

  function setButtonState(button, state, message) {
    if (!button) return;
    const currentTimer = buttonStateTimers.get(button);
    if (currentTimer) {
      window.clearTimeout(currentTimer);
      buttonStateTimers.delete(button);
    }
    if (state === 'adding') {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-busy');
      if (button.disabled || button.dataset.disabledByVariant === 'true') {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
    }
    if (state === 'idle') delete button.dataset.addState;
    else button.dataset.addState = state;
    if (state === 'error' && message) {
      button.setAttribute('data-error', message);
      renderButtonError(button, message);
    } else {
      button.removeAttribute('data-error');
      renderButtonError(button, '');
    }
  }

  function renderButtonError(button, message) {
    const existing = button.parentElement?.querySelector(':scope > [data-add-to-cart-error]');
    if (!message) {
      existing?.remove();
      return;
    }
    const note = existing || document.createElement('p');
    if (!existing) {
      note.className = 'yise-cart__add-error';
      note.setAttribute('data-add-to-cart-error', '');
      note.setAttribute('role', 'alert');
      note.setAttribute('aria-atomic', 'true');
      button.insertAdjacentElement('afterend', note);
    }
    note.textContent = message;
  }

  class AddToCartComponent extends Component {
    handleClick(event) {
      if (this.closest('form')) return;
      event.preventDefault();
      const variantId = Number(this.dataset.variantId || 0);
      if (!variantId) return;
      const button = this.querySelector('button') || this;
      if (remainingAllowance(cart.get().cart, variantId, cartMaxItemQuantity()) < 1) {
        const message = maxQuantityMessage(button);
        setButtonState(button, 'max', message);
        scheduleButtonState(button, 'idle', 2000);
        return;
      }
      const item = { id: variantId, quantity: 1 };
      const price = Number(this.dataset.variantPrice);
      if (this.dataset.variantPrice !== undefined && Number.isFinite(price) && price >= 0) {
        item.price = price;
      }
      const presentation = optimisticLinePresentation(this, item);
      if (presentation) item.__presentation = presentation;
      cart.add([item], { context: 'product', contextEl: this }).catch((error) => {
        setButtonState(button, 'error', error?.message);
        scheduleButtonState(button, 'idle', 4000);
      });
      rememberCartAddTrigger(button);
      openCartDrawer();
    }
  }

  class QuantitySelectorComponent extends Component {
    get input() { return this.querySelector('input[type="number"], input[inputmode="numeric"]'); }

    clamp(value) {
      const input = this.input;
      const min = Number(input.min || 0);
      let max = input.max === '' ? Infinity : Number(input.max);
      const maxItemQuantity = cartMaxItemQuantity();
      if (maxItemQuantity) max = Math.min(max, maxItemQuantity);
      return Math.max(min, Math.min(max, value));
    }

    increaseQuantity() { this.applyValue(Number(this.input.value || 0) + 1); }
    decreaseQuantity() { this.applyValue(Number(this.input.value || 0) - 1); }
    setQuantity() { this.applyValue(Number(this.input.value || 0)); }
    selectInputValue() { this.input.select(); }

    applyValue(next) {
      const value = this.clamp(next);
      this.input.value = value;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function normalizedPlanToken(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function planFrequency(plan) {
    const match = `${plan?.name || ''} ${plan?.fullName || ''}`.match(/\d+(?:\.\d+)?/);
    return match ? Number(match[0]) : Number.POSITIVE_INFINITY;
  }

  class PurchaseOptionsComponent extends Component {
    ready() {
      const productForm = this.closest('product-form-component');
      this.variantState = {
        id: this.dataset.variantId,
        available: !this.subscriptionUI?.hidden,
        requiresSellingPlan: this.dataset.requiresSellingPlan === 'true',
        waitlistOverrideActive: false,
        price: Number(this.dataset.basePrice || 0),
        formattedPrice: this.dataset.baseFormattedPrice || '',
        discountPricing: jsonValue(productForm?.dataset.cartLineDiscountPricing),
        sellingPlans: this.plansFromOptions(),
      };

      if (this.oneTimeInput) this.oneTimeInput.disabled = Boolean(this.variantState.requiresSellingPlan);
      if (this.subscriptionInput) {
        this.subscriptionInput.disabled = this.dataset.subscriptionAvailable !== 'true';
      }

      this.listen(this, 'change', (event) => {
        const control = event.target;
        if (!(control instanceof HTMLInputElement || control instanceof HTMLSelectElement)) return;
        if (!control.matches('[data-purchase-mode], select[name="selling_plan"]')) return;
        this.syncMode();
      });

      this.sortCurrentOptions();
      this.applyDeepLinkPreference();
      this.syncMode();
    }

    get subscriptionUI() {
      return this.querySelector('[data-subscription-ui]');
    }

    get subscriptionDetails() {
      return this.querySelector('[data-subscription-details]');
    }

    get oneTimeInput() {
      return this.querySelector('[data-purchase-mode="one_time"]');
    }

    get subscriptionInput() {
      return this.querySelector('[data-purchase-mode="subscription"]');
    }

    get sellingPlanSelect() {
      return this.querySelector('select[name="selling_plan"]');
    }

    get saveLabel() {
      return this.querySelector('[data-purchase-save-label]');
    }

    plansFromOptions() {
      return [...(this.sellingPlanSelect?.options || [])].map((option) => ({
        id: Number(option.value),
        name: option.dataset.planName || option.textContent.trim(),
        fullName: option.dataset.planFullName || option.textContent.trim(),
        cartLabel: option.dataset.planCartLabel || '',
        defaultSelected: option.dataset.planDefault === 'true',
        purchaseSaveLabel: option.dataset.planPurchaseSaveLabel || '',
        purchaseSavePrevious: option.dataset.planPurchaseSavePrevious || '',
        price: Number(option.dataset.planPrice || 0),
        compareAtPrice: finiteNumber(option.dataset.planCompareAtPrice),
        formattedPrice: option.dataset.planFormattedPrice || '',
        formattedCompareAtPrice: option.dataset.planFormattedCompareAtPrice || '',
        saveLabel: option.dataset.planSaveLabel || '',
        discountPricing: jsonValue(option.dataset.planDiscountPricing),
      }));
    }

    sortedPlans(plans) {
      return [...plans].sort((left, right) => {
        const leftFrequency = planFrequency(left);
        const rightFrequency = planFrequency(right);
        if (leftFrequency !== rightFrequency) return leftFrequency - rightFrequency;
        return String(left.name).localeCompare(String(right.name));
      });
    }

    sortCurrentOptions() {
      const select = this.sellingPlanSelect;
      if (!select) return;
      const selectedId = select.value;
      this.rebuildPlanOptions(this.plansFromOptions(), selectedId);
    }

    rebuildPlanOptions(plans, selectedId) {
      const select = this.sellingPlanSelect;
      if (!select) return;

      const fragment = document.createDocumentFragment();
      for (const plan of this.sortedPlans(plans || [])) {
        const option = new Option(plan.name || plan.fullName || '', String(plan.id));
        option.dataset.planName = plan.name || '';
        option.dataset.planFullName = plan.fullName || '';
        option.dataset.planCartLabel = plan.cartLabel || '';
        option.dataset.planDefault = String(Boolean(plan.defaultSelected));
        option.dataset.planPurchaseSaveLabel = plan.purchaseSaveLabel
          || this.dataset.subscriptionSavingsText
          || '';
        option.dataset.planPurchaseSavePrevious = plan.purchaseSaveLabel
          ? (plan.purchaseSavePrevious || '')
          : '';
        option.dataset.planPrice = String(plan.price ?? '');
        option.dataset.planCompareAtPrice = String(plan.compareAtPrice ?? '');
        option.dataset.planFormattedPrice = plan.formattedPrice || '';
        option.dataset.planFormattedCompareAtPrice = plan.formattedCompareAtPrice || '';
        option.dataset.planSaveLabel = plan.saveLabel || '';
        option.dataset.planDiscountPricing = JSON.stringify(plan.discountPricing || null);
        fragment.append(option);
      }
      select.replaceChildren(fragment);

      const retained = [...select.options].some((option) => option.value === String(selectedId || ''));
      if (retained) select.value = String(selectedId);
      else {
        const defaultOption = [...select.options].find((option) => option.dataset.planDefault === 'true');
        if (defaultOption) select.value = defaultOption.value;
        else if (select.options.length) select.selectedIndex = 0;
      }
    }

    applyDeepLinkPreference() {
      const parameter = this.dataset.deepLinkParameter;
      if (!parameter || !this.subscriptionInput || this.subscriptionInput.disabled) return;
      const requested = normalizedPlanToken(new URLSearchParams(window.location.search).get(parameter));
      if (!requested) return;

      const option = [...(this.sellingPlanSelect?.options || [])].find((candidate) => {
        const name = normalizedPlanToken(candidate.dataset.planName);
        const fullName = normalizedPlanToken(candidate.dataset.planFullName);
        return name === requested || fullName === requested || name.includes(requested) || fullName.includes(requested);
      });
      if (!option) return;

      this.sellingPlanSelect.value = option.value;
      this.subscriptionInput.checked = true;
    }

    syncVariant(variant) {
      if (!variant) {
        this.variantState = null;
        this.dataset.variantId = '';
        this.rebuildPlanOptions([], null);
        if (this.subscriptionUI) this.subscriptionUI.hidden = true;
        if (this.subscriptionDetails) this.subscriptionDetails.dataset.open = 'false';
        if (this.oneTimeInput) this.oneTimeInput.disabled = true;
        if (this.subscriptionInput) this.subscriptionInput.disabled = true;
        if (this.sellingPlanSelect) {
          this.sellingPlanSelect.disabled = true;
          this.sellingPlanSelect.setAttribute('aria-disabled', 'true');
        }
        this.updateSaveLabel(null);
        return;
      }
      const selectedPlanId = this.sellingPlanSelect?.value;
      const plans = Array.isArray(variant.sellingPlans) ? variant.sellingPlans : [];
      const subscriptionAvailable = Boolean(
        variant.available
        && !variant.waitlistOverrideActive
        && plans.length
      );

      this.variantState = variant;
      this.dataset.variantId = String(variant.id || '');
      this.dataset.basePrice = String(variant.price ?? '');
      this.dataset.baseFormattedPrice = variant.formattedPrice || '';
      this.rebuildPlanOptions(plans, selectedPlanId);

      if (this.subscriptionUI) this.subscriptionUI.hidden = !subscriptionAvailable;
      if (this.oneTimeInput) {
        this.oneTimeInput.disabled = !variant.available || Boolean(variant.requiresSellingPlan);
      }
      if (this.subscriptionInput) {
        this.subscriptionInput.disabled = !subscriptionAvailable;
      }

      if (variant.requiresSellingPlan && subscriptionAvailable) {
        this.subscriptionInput.checked = true;
      } else if (!subscriptionAvailable && this.oneTimeInput) {
        this.oneTimeInput.checked = true;
      }

      this.syncMode();
    }

    syncMode() {
      const select = this.sellingPlanSelect;
      const subscriptionActive = Boolean(
        this.subscriptionInput?.checked
        && !this.subscriptionInput.disabled
        && select?.options.length
      );

      if (this.subscriptionDetails) this.subscriptionDetails.dataset.open = String(subscriptionActive);
      if (select) {
        select.disabled = !subscriptionActive;
        select.setAttribute('aria-disabled', String(!subscriptionActive));
      }

      this.syncQuantityVisibility(subscriptionActive);

      const plans = this.plansFromOptions();
      const selectedPlan = plans.find((plan) => String(plan.id) === String(select?.value)) || null;
      this.updateSaveLabel(selectedPlan || plans[0]);
      this.updatePurchasePrice(subscriptionActive ? selectedPlan : null);
    }

    /**
     * The Quantity selector setting scopes the control to a purchase mode, so the
     * row re-resolves whenever the mode changes.
     */
    syncQuantityVisibility(subscriptionActive) {
      const display = this.dataset.quantityDisplay || 'both';
      const visible = display === 'both'
        || (display === 'subscription' && subscriptionActive)
        || (display === 'one_time' && !subscriptionActive);

      this.dataset.hasQuantity = String(visible);
      if (visible) return;

      const input = this.querySelector('[data-purchase-quantity] input[name="quantity"]');
      if (input && input.value !== '1') input.value = '1';
    }

    updateSaveLabel(plan) {
      if (!this.saveLabel) return;
      const label = plan?.purchaseSaveLabel || '';
      const previous = label ? (plan?.purchaseSavePrevious || '') : '';
      const labelText = this.saveLabel.querySelector('[data-purchase-save-text]');
      const previousText = this.saveLabel.querySelector('[data-purchase-save-previous]');

      if (labelText) labelText.textContent = label;
      else this.saveLabel.textContent = label;

      if (previousText) {
        previousText.textContent = previous;
        previousText.hidden = !previous;
      }

      this.saveLabel.hidden = !label;
    }

    updatePurchasePrice(plan) {
      const productForm = this.closest('product-form-component');
      if (!productForm) return;
      const price = plan?.price ?? this.variantState?.price ?? Number(this.dataset.basePrice || 0);
      const formattedPrice = plan?.formattedPrice
        || this.variantState?.formattedPrice
        || this.dataset.baseFormattedPrice
        || '';
      const formattedCompareAtPrice = plan?.formattedCompareAtPrice || '';
      const discountPricing = plan?.discountPricing || this.variantState?.discountPricing || null;

      productForm.dataset.variantPrice = String(price);
      if (discountPricing) {
        productForm.dataset.cartLineDiscountPricing = JSON.stringify(discountPricing);
      } else {
        delete productForm.dataset.cartLineDiscountPricing;
      }
      const discountPrice = productForm.querySelector('[data-variant-discount-price]');
      if (discountPrice) {
        if (plan?.discountPricing) discountPrice.setPricingOverride?.(plan.discountPricing);
        else if (plan) {
          discountPrice.setPriceOverride?.(
            formattedPrice,
            formattedCompareAtPrice,
            price,
            plan.compareAtPrice,
          );
        }
        else discountPrice.clearPriceOverride?.();
      } else {
        for (const priceElement of productForm.querySelectorAll('[data-button-price]')) {
          priceElement.textContent = formattedPrice;
        }
        for (const comparePrice of productForm.querySelectorAll('[data-button-compare-price]')) {
          const comparePriceValue = comparePrice.querySelector('[data-button-compare-price-value]');
          if (comparePriceValue) comparePriceValue.textContent = formattedCompareAtPrice;
          comparePrice.hidden = !formattedCompareAtPrice;
        }
        for (const regularLabel of productForm.querySelectorAll('[data-button-price-label-regular]')) {
          regularLabel.hidden = Boolean(formattedCompareAtPrice);
        }
        for (const saleLabel of productForm.querySelectorAll('[data-button-price-label-sale]')) {
          saleLabel.hidden = !formattedCompareAtPrice;
        }
      }

      const details = productForm.closest('[data-product-details]');
      if (details) details.dataset.price = formattedPrice;
    }
  }

  class CartQuantitySelectorComponent extends QuantitySelectorComponent {
    get row() { return this.closest('[data-key]'); }

    get busy() { return Boolean(this.row?.hasAttribute('data-line-busy')); }

    increaseQuantity() {
      if (this.busy) return;
      this.lineIntent = 'increase';
      super.increaseQuantity();
    }

    decreaseQuantity() {
      if (this.busy) return;
      this.lineIntent = 'decrease';
      const current = Number(this.input.value || 0);
      if (current > Number(this.input.min || 1)) {
        this.applyValue(current - 1);
        return;
      }
      const row = this.row;
      if (!row?.querySelector('[data-remove-item]')) {
        this.lineIntent = null;
        return;
      }
      this.lineIntent = null;
      this.closest('cart-items-component')?.removeLine(row);
    }

    setQuantity() {
      if (this.busy) return;
      super.setQuantity();
    }

    applyValue(next) {
      const value = this.clamp(next);
      this.input.value = value;
      const key = this.input?.dataset.cartLine || this.dataset.cartLine;
      if (!key) return;
      const intent = this.lineIntent || (value === 0 ? 'remove' : null);
      this.lineIntent = null;
      const currentLine = cart.get().cart.items.find((line) => line.key === key);
      if (currentLine && Number(currentLine.quantity) === value) return;
      const items = this.closest('cart-items-component');
      const title = cartLineTitle(this.row);
      items?.noteLineIntent(key, intent);
      cart.changeLine(key, { quantity: value }, { debounce: 250, context: 'cart', contextEl: this })
        .then(async () => {
          await cart.settled();
          const settledCart = cart.get().cart;
          const line = settledCart.items.find((item) => item.key === key);
          if (!line || Number(line.quantity) !== value) return;
          announce(
            this.closest('cart-drawer')?.liveRegion,
            `${title} quantity ${value}. Bag total ${money(settledCart.total_price)}.`,
          );
        })
        .catch((error) => items?.showLineError(key, error?.message));
    }
  }

  defineElement('product-form-component', ProductFormComponent);
  defineElement('add-to-cart-component', AddToCartComponent);
  defineElement('quantity-selector-component', QuantitySelectorComponent);
  defineElement('variant-discount-price', VariantDiscountPrice);
  defineElement('purchase-options', PurchaseOptionsComponent);
  defineElement('cart-quantity-selector-component', CartQuantitySelectorComponent);

  /** The upsell surface loads its own module from a script inside the drawer content, and a
   *  script that arrives through a morph never executes — so the deferred path imports it here. */
  function loadCartUpsells() {
    (window.importShim ?? ((specifier) => import(specifier)))('@theme/cart-upsell').catch(() => {});
  }

  const registeredDrawerSections = new Set();
  function ensureDrawerSection(sectionId) {
    const id = sectionId || CART_SECTION_ID;
    if (registeredDrawerSections.has(id)) return;
    registeredDrawerSections.add(id);
    cart.registerSection(id, (html) => {
      const el = document.getElementById('cart-drawer');
      if (el && (el.dataset.sectionId || CART_SECTION_ID) === id && typeof el.morphContent === 'function') {
        el.morphContent(html);
      }
    });
  }

  class CartDrawerComponent extends Component {
    ready() {
      if (document.readyState === 'loading') {
        this.listen(document, 'DOMContentLoaded', () => this.ready(), { once: true });
        return;
      }
      if (!this.panel || !this.liveRegion) {
        this.contentReadyObserver ??= new MutationObserver(() => {
          if (!this.panel || !this.liveRegion) return;
          this.contentReadyObserver.disconnect();
          this.contentReadyObserver = null;
          if (this.isConnected) this.ready();
        });
        this.contentReadyObserver.observe(this, { childList: true, subtree: true });
        return;
      }
      this.heldContentMorph ??= null;
      this.inertRecords ??= [];
      const panel = this.panel;
      // A native <dialog> reports Escape and programmatic closes through 'close'.
      this.listen(panel, 'close', () => this.handleClose());
      this.listen(panel, 'cancel', (event) => { event.preventDefault(); this.close(); });
      this.listen(panel, 'click', (event) => { if (isBackdropClick(panel, event)) this.close(); });
      this.listen(panel, 'keydown', (event) => this.handleKeydown(event));
      this.listen(this, 'click', (event) => this.handleClick(event));
      this.listen(this, 'change', (event) => this.handleGiftToggle(event));
      this.listen(this, 'focusout', (event) => this.handleGiftMessageBlur(event));
      this.listen(window, 'pagehide', () => this.saveGiftNoteBeacon());
      this.listen(this, 'click', (event) => {
        if (!checkoutTriggerFromEvent(event)) return;
        if (this.querySelector('[data-checkout]')?.getAttribute('aria-disabled') !== 'true') return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, { capture: true });
      this.listen(this, 'scroll', (event) => {
        if (event.target === this.scrollRegion) this.scheduleScrollStateUpdate();
      }, { capture: true, passive: true });

      if (typeof ResizeObserver === 'function') {
        this.scrollResizeObserver = new ResizeObserver(() => this.scheduleScrollStateUpdate());
      }
      this.observeScrollGeometry();

      ensureDrawerSection(this.dataset.sectionId);
      this.unsubscribe?.();
      this.unsubscribe = cart.subscribe((snapshot) => this.render(snapshot));

      this.refreshTriggers();
      this.listen(document, 'shopify:section:load', () => this.refreshTriggers());
      this.scheduleLocationAlert();
      this.scheduleContentHydration();

      const params = new URLSearchParams(window.location.search);
      if (params.get('cart') === 'open') {
        params.delete('cart');
        const query = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : ''));
        this.open();
      }
    }

    teardown() {
      this.contentReadyObserver?.disconnect();
      this.contentReadyObserver = null;
      this.contentHydration = null;
      window.clearTimeout(this.hydrationRetryTimer);
      this.hydrationRetryTimer = null;
      window.clearTimeout(this.hydrationFallbackTimer);
      this.hydrationFallbackTimer = null;
      window.clearTimeout(this.locationAlertTimer);
      this.locationAlertTimer = null;
      this.triggerAbortController?.abort();
      this.triggerAbortController = null;
      this.prefetchAbortController?.abort();
      this.prefetchAbortController = null;
      this.unsubscribe?.();
      this.unsubscribe = null;
      this.scrollResizeObserver?.disconnect();
      this.scrollResizeObserver = null;
      if (this.scrollStateKey) window.YiseFrame?.cancelMeasure(this.scrollStateKey);
      this.setBackgroundInert(false);
      this.removeAttribute('data-scroll-at-end');
      document.documentElement.removeAttribute('data-cart-drawer-open');
      document.body.classList.remove('mini-cart-open');
    }

    /**
     * The flyout panel. `sections/cart-drawer.liquid` ships YISE's `div#mini-cart[role="dialog"]`
     * so `snippets/cart-drawer-css.liquid`'s `#mini-cart.yise-cart` geometry and `translateX` slide apply
     * unchanged; the `<dialog>` branch stays for markup that opts into the native element instead.
     */
    get panel() { return this.querySelector('dialog, [role="dialog"]'); }

    get dialog() { return this.panel; }

    get isOpen() { return panelIsOpen(this.panel); }

    get liveRegion() { return cartLiveRegion(this); }

    get scrollRegion() { return this.querySelector('[data-cart-scroll]'); }

    /** The loading frame a page render ships in place of the drawer's real content, or null once hydrated. */
    get pendingContent() {
      return this.querySelector('[data-cart-drawer-content][data-cart-drawer-pending]');
    }

    /**
     * Fetch the real drawer content once the page has finished loading. A page render ships only
     * `cart-drawer-loading`, so nothing below competes with the critical path; an open or a trigger
     * hover pulls this forward.
     */
    scheduleContentHydration() {
      if (!this.pendingContent) return;
      if (document.readyState === 'complete') {
        this.hydrateContent();
        return;
      }
      this.listen(window, 'load', () => this.hydrateContent(), { once: true });
      // Third-party tags can hold window load open long after the page is usable.
      window.clearTimeout(this.hydrationFallbackTimer);
      this.hydrationFallbackTimer = window.setTimeout(
        () => this.hydrateContent(),
        CART_HYDRATION_LOAD_FALLBACK_MS,
      );
    }

    hydrateContent() {
      if (!this.pendingContent || this.contentHydration) return;
      this.prefetchAbortController?.abort();
      this.prefetchAbortController = null;
      loadCartUpsells();
      // An open before DOMContentLoaded lands here ahead of ready(), and a resync with no
      // registered section declines at once.
      ensureDrawerSection(this.dataset.sectionId);
      this.contentHydration = cart.settled()
        .then(() => cart.resyncSections())
        .finally(() => {
          this.contentHydration = null;
          this.scheduleHydrationRetry();
        });
    }

    /**
     * A resync declines while a mutation is in flight and fails outright on a bad response, and
     * reports both the same way — so the retry is what tells them apart. When the attempts run out
     * the frame offers the cart page, because a drawer that spins forever has taken the bag away.
     */
    scheduleHydrationRetry() {
      const content = this.pendingContent;
      if (!content || this.hydrationRetryTimer) return;
      this.hydrationRetries ??= 0;
      if (this.hydrationRetries >= CART_HYDRATION_MAX_RETRIES) {
        const frame = content.querySelector('[data-cart-drawer-loading]');
        frame?.setAttribute('data-cart-drawer-loading-failed', '');
        frame?.querySelector('[data-cart-drawer-loading-label]')?.remove();
        // The region is not busy any more, it is finished and empty-handed. `data-cart-drawer-pending`
        // stays, so a later mutation or a reopen is still recognised as the hydration it is.
        content.removeAttribute('aria-busy');
        return;
      }
      this.hydrationRetries += 1;
      this.hydrationRetryTimer = window.setTimeout(() => {
        this.hydrationRetryTimer = null;
        this.hydrateContent();
      }, CART_HYDRATION_RETRY_MS * this.hydrationRetries);
    }

    /**
     * Retire the loading frame's busy state once the fetched content has landed. The cart view
     * event is re-dispatched when the drawer is already open, because `dispatchViewEvent` reads its
     * payload out of the content and an open that raced hydration found nothing to report.
     */
    revealHydratedContent(content) {
      content.removeAttribute('data-cart-drawer-pending');
      content.removeAttribute('aria-busy');
      loadCartUpsells();
      if (this.isOpen) this.dispatchViewEvent();
    }

    observeScrollGeometry() {
      this.scrollResizeObserver?.disconnect();
      const scrollRegion = this.scrollRegion;
      if (!scrollRegion) {
        this.removeAttribute('data-scroll-at-end');
        return;
      }
      this.scrollResizeObserver?.observe(scrollRegion);
      const filledState = scrollRegion.querySelector('[data-filled-state]');
      if (filledState) this.scrollResizeObserver?.observe(filledState);
      this.scheduleScrollStateUpdate();
    }

    /** Return the drawer's own scroll region to the top of the line list. The
     *  drawer's blanket reduced-motion rule zeroes CSS `scroll-behavior`, but a
     *  `behavior` passed here outranks it, so the preference is read again. */
    scrollToTop() {
      this.scrollRegion?.scrollTo({ top: 0, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }

    measureScrollState() {
      const scrollRegion = this.scrollRegion;
      if (!scrollRegion) return null;
      const maxScrollTop = Math.max(0, scrollRegion.scrollHeight - scrollRegion.clientHeight);
      const scrollTop = Math.min(maxScrollTop, Math.max(0, scrollRegion.scrollTop));
      return maxScrollTop - scrollTop <= 1;
    }

    scheduleScrollStateUpdate() {
      const frame = window.YiseFrame;
      if (!frame) {
        const atEnd = this.measureScrollState();
        if (atEnd === null) this.removeAttribute('data-scroll-at-end');
        else this.toggleAttribute('data-scroll-at-end', atEnd);
        return;
      }
      frame.measure(this.scrollStateKey ??= Symbol('cart-drawer-scroll-state'), () => {
        const atEnd = this.measureScrollState();
        frame.write(() => {
          if (atEnd === null) this.removeAttribute('data-scroll-at-end');
          else this.toggleAttribute('data-scroll-at-end', atEnd);
        });
      });
    }

    /**
     * Trigger, dismiss, and cart:open events share one abortable listener scope.
     */
    refreshTriggers() {
      this.triggerAbortController?.abort();
      this.triggerAbortController = new AbortController();
      const signal = this.triggerAbortController.signal;
      window.addEventListener('click', (event) => this.handleTriggerClick(event), {
        capture: true,
        signal,
      });
      window.addEventListener('cart:open', (event) => {
        event.stopImmediatePropagation();
        this.open();
      }, { capture: true, signal });
      this.refreshContentPrefetch();
      updateTriggerCounts(cart.get().cart.item_count);
    }

    /**
     * Reaching for the cart is the earliest honest signal that the content is about to be needed,
     * so a hover or a tab onto any trigger pulls hydration ahead of the click.
     *
     * `pointerenter` does not bubble but still travels the capture phase, which is what lets one
     * window listener cover every trigger. It also fires for every element the pointer enters, so
     * these come straight back off as soon as there is nothing left to prefetch.
     */
    refreshContentPrefetch() {
      this.prefetchAbortController?.abort();
      this.prefetchAbortController = null;
      if (!this.pendingContent) return;
      this.prefetchAbortController = new AbortController();
      const signal = this.prefetchAbortController.signal;
      const prefetch = (event) => {
        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest(CART_TRIGGER_SELECTOR)) this.hydrateContent();
      };
      window.addEventListener('pointerenter', prefetch, { capture: true, signal });
      window.addEventListener('focusin', prefetch, { capture: true, signal });
    }

    /**
     * Claims a cart toggle without stopping the event.
     *
     * The click still has to reach the analytics listener and the mega-menu
     * closer, both of which register later on `document`. Marking the event is
     * what keeps the drawer from toggling twice; `stopImmediatePropagation`
     * here would silently drop `Cart - Open` and leave the mega menu up.
     */
    handleTriggerClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || event.yiseCartToggleHandled) return;
      const closer = target.closest('[data-cart-close]');
      if (closer && this.contains(closer)) {
        event.preventDefault();
        event.yiseCartToggleHandled = true;
        this.close();
        return;
      }
      const trigger = target.closest(CART_TRIGGER_SELECTOR);
      if (!trigger || this.contains(trigger)) return;
      event.preventDefault();
      event.yiseCartToggleHandled = true;
      if (this.isOpen) this.close();
      else this.open(trigger);
    }

    /**
     * Open on a beat after an add, so the button's own feedback reads before the
     * drawer slides in and any gift the reconciler owes the cart lands first.
     */
    openForAdd() {
      const trigger = this.pendingAddTrigger;
      this.pendingAddTrigger = null;
      if (this.isOpen || this.deferredAddOpenTimer) return;
      this.deferredAddOpenTimer = window.setTimeout(() => {
        this.deferredAddOpenTimer = null;
        this.open(trigger);
      }, CART_ADD_OPEN_DELAY_MS);
    }

    rememberAddTrigger(trigger) {
      this.pendingAddTrigger = trigger instanceof HTMLElement ? trigger : null;
    }

    cancelAddOpen(trigger) {
      this.pendingAddTrigger = null;
      window.clearTimeout(this.deferredAddOpenTimer);
      this.deferredAddOpenTimer = null;
      if (this.isOpen && this.activeTrigger === trigger) this.close();
    }

    open(trigger) {
      const panel = this.panel;
      if (!panel || panelIsOpen(panel)) return;
      // Opening is a fresh ask, so a drawer that gave up earlier gets its attempts back.
      this.hydrationRetries = 0;
      this.hydrateContent();
      window.clearTimeout(this.deferredAddOpenTimer);
      this.deferredAddOpenTimer = null;
      this.activeTrigger = trigger instanceof HTMLElement ? trigger : null;
      this.returnFocus = this.activeTrigger
        ? null
        : (document.activeElement instanceof HTMLElement ? document.activeElement : null);
      this.querySelector('cart-items-component')?.preparePendingOptimisticRows?.();
      eagerLoadCartItemImages(panel);
      panel.scrollTo?.(0, 0);
      this.scrollRegion?.scrollTo(0, 0);
      this.showPanel(panel);
      this.setExpanded(true);
      this.scheduleScrollStateUpdate();
      focusWhenVisible(this.querySelector('[data-cart-close]'));
      this.dispatchViewEvent();
    }

    /**
     * YISE's open state is `body.mini-cart-open` plus the panel's own inert/aria-hidden pair — the
     * shape the drawer ships, because the slide-in is a CSS transform that a top-layer `<dialog>`
     * cannot run from `display: none`. Escape, the Tab trap, background `inert` and the scroll lock
     * are therefore this component's to own (`handleKeydown`, `trapTab`, `setBackgroundInert`, and
     * `body.mini-cart-open { overflow: hidden }` in `snippets/cart-drawer-css.liquid`). A native `<dialog>`
     * gets `showModal()` and keeps the body class so the same styling and third-party code work.
     */
    showPanel(panel) {
      if (isNativeDialog(panel)) {
        panel.showModal();
      } else {
        panel.removeAttribute('inert');
        panel.inert = false;
        panel.setAttribute('aria-hidden', 'false');
        this.setBackgroundInert(true);
      }
      panel.dataset.cartPanelOpen = 'true';
      document.body.classList.add('mini-cart-open');
      document.documentElement.setAttribute('data-cart-drawer-open', 'true');
    }

    close() {
      const panel = this.panel;
      if (!panel || !panelIsOpen(panel)) return;
      if (isNativeDialog(panel)) {
        panel.close();
        return;
      }
      this.handleClose();
    }

    toggle() { this.isOpen ? this.close() : this.open(); }

    handleClose() {
      const panel = this.panel;
      if (panel) {
        delete panel.dataset.cartPanelOpen;
        if (!isNativeDialog(panel)) {
          panel.inert = true;
          panel.setAttribute('inert', '');
          panel.setAttribute('aria-hidden', 'true');
        }
      }
      this.setBackgroundInert(false);
      document.body.classList.remove('mini-cart-open');
      document.documentElement.removeAttribute('data-cart-drawer-open');
      this.setExpanded(false);
      const restore = this.activeTrigger?.isConnected ? this.activeTrigger : this.returnFocus;
      this.activeTrigger = null;
      this.returnFocus = null;
      if (restore?.isConnected && !restore.inert) restore.focus();
    }

    /**
     * Everything outside the drawer is taken out of the accessibility tree while
     * a non-modal panel is open, and put back exactly as authored on close.
     */
    setBackgroundInert(isOpen) {
      this.inertRecords ??= [];
      if (isOpen) {
        if (this.inertRecords.length) return;
        let branch = this;
        while (branch && branch.parentElement) {
          for (const sibling of branch.parentElement.children) {
            if (sibling === branch) continue;
            if (sibling.tagName === 'SCRIPT' || sibling.tagName === 'STYLE') continue;
            this.inertRecords.push({
              element: sibling,
              inert: sibling.inert,
              inertAttribute: sibling.getAttribute('inert'),
              ariaHidden: sibling.getAttribute('aria-hidden'),
            });
            sibling.setAttribute('inert', '');
            sibling.inert = true;
            sibling.setAttribute('aria-hidden', 'true');
          }
          if (branch.parentElement === document.body) break;
          branch = branch.parentElement;
        }
        return;
      }
      for (const record of this.inertRecords) {
        record.element.inert = record.inert;
        if (record.inertAttribute == null) record.element.removeAttribute('inert');
        else record.element.setAttribute('inert', record.inertAttribute);
        if (record.ariaHidden == null) record.element.removeAttribute('aria-hidden');
        else record.element.setAttribute('aria-hidden', record.ariaHidden);
      }
      this.inertRecords = [];
    }

    handleKeydown(event) {
      const panel = this.panel;
      if (!panel) return;
      if (event.key === 'Escape' && !isNativeDialog(panel)) {
        event.preventDefault();
        this.close();
        return;
      }
      trapTab(panel, event);
    }

    handleClick(event) {
      const heldCheckout = event.target.closest('[data-checkout][aria-disabled="true"]');
      if (heldCheckout) {
        event.preventDefault();
        return;
      }
      const checkout = checkoutTriggerFromEvent(event);
      if (checkout && this.giftNoteIsDirty()) {
        event.preventDefault();
        this.saveGiftNoteAndCheckout();
        return;
      }
      if (event.target.closest('[data-cart-close]')) {
        event.preventDefault();
        this.close();
        return;
      }
      const removeDiscountBtn = event.target.closest('[data-remove-discount]');
      if (removeDiscountBtn) {
        const discountComponent = this.querySelector('cart-discount-component');
        if (discountComponent
          && !discountComponent.contains(removeDiscountBtn)
          && typeof discountComponent.removeDiscount === 'function') {
          discountComponent.removeDiscount(event);
        }
        return;
      }
      const addButton = event.target.closest('[data-add-bundle], [data-add-recommendation], [data-cart-threshold-add]');
      if (addButton) this.addProduct(addButton);
    }

    giftSection() { return this.querySelector('[data-cart-gift-section]'); }

    giftLines(cartState = cart.get().cart) {
      return (cartState?.items || []).filter(
        (line) => line.properties?.['_gift-check-box-trigger'] === 'true',
      );
    }

    giftNoteIsDirty() {
      const section = this.giftSection();
      const toggle = section?.querySelector('[data-cart-gift-toggle]');
      const textarea = section?.querySelector('[data-cart-gift-message]');
      return Boolean(toggle?.checked && textarea && textarea.value !== String(cart.get().cart.note || ''));
    }

    handleGiftToggle(event) {
      const toggle = event.target.closest?.('[data-cart-gift-toggle]');
      if (!toggle || !this.contains(toggle) || this.giftBusy) return;
      this.setGiftEnabled(toggle.checked);
    }

    handleGiftMessageBlur(event) {
      const textarea = event.target.closest?.('[data-cart-gift-message]');
      if (!textarea || !this.contains(textarea) || !this.giftNoteIsDirty()) return;
      this.saveGiftNote().catch((error) => announce(this.liveRegion, error?.message, true));
    }

    setGiftControlsBusy(busy) {
      const section = this.giftSection();
      section?.toggleAttribute('aria-busy', busy);
      const toggle = section?.querySelector('[data-cart-gift-toggle]');
      const textarea = section?.querySelector('[data-cart-gift-message]');
      const loader = section?.querySelector('[data-cart-gift-loader]');
      if (toggle) toggle.disabled = busy;
      if (textarea) textarea.disabled = busy;
      if (loader) loader.hidden = !busy;
    }

    async setGiftEnabled(enabled) {
      const section = this.giftSection();
      if (!section) return;
      this.giftBusy = true;
      this.setGiftControlsBusy(true);
      const title = section.dataset.giftProductTitle || 'gift';
      try {
        if (enabled) {
          await cart.setAttributes({ gift: 'true' }, { source: 'gift-options', context: 'cart', contextEl: this });
          const variantId = Number(section.dataset.giftVariantId || 0);
          if (variantId && this.giftLines().length === 0) {
            const note = section.querySelector('[data-cart-gift-message]')?.value || '';
            await cart.add([{
              id: variantId,
              quantity: 1,
              properties: { '_gift-check-box-trigger': 'true', 'Gift message': note },
            }], { source: 'gift-options', context: 'cart', contextEl: this });
            announce(this.liveRegion, `Added ${title} to bag.`);
          }
        } else {
          for (const line of this.giftLines()) {
            await cart.changeLine(
              line.key,
              { quantity: 0 },
              { source: 'gift-options', context: 'cart', contextEl: this },
            );
          }
          await cart.setAttributes({ gift: '' }, { source: 'gift-options', context: 'cart', contextEl: this });
          await cart.setNote('', { source: 'gift-options', context: 'cart', contextEl: this });
          announce(this.liveRegion, `Removed ${title} from bag.`);
        }
      } catch (error) {
        announce(this.liveRegion, error?.message || `Unable to update ${title}.`, true);
      } finally {
        this.giftBusy = false;
        this.setGiftControlsBusy(false);
        this.syncGiftOptions(cart.get());
      }
    }

    async saveGiftNote() {
      const section = this.giftSection();
      const textarea = section?.querySelector('[data-cart-gift-message]');
      if (!textarea) return;
      const note = textarea.value;
      if (note === String(cart.get().cart.note || '')) return;
      this.giftBusy = true;
      this.setGiftControlsBusy(true);
      try {
        await cart.setNote(note, { source: 'gift-options', context: 'cart', contextEl: this });
        const line = this.giftLines()[0];
        if (line && line.properties?.['Gift message'] !== note) {
          await cart.changeLine(line.key, {
            properties: { ...line.properties, 'Gift message': note },
          }, { source: 'gift-options', context: 'cart', contextEl: this });
        }
      } finally {
        this.giftBusy = false;
        this.setGiftControlsBusy(false);
      }
    }

    async saveGiftNoteAndCheckout() {
      if (this.giftCheckoutPending) return;
      this.giftCheckoutPending = true;
      try {
        await this.saveGiftNote();
      } catch (error) {
        announce(this.liveRegion, error?.message || 'Unable to save the gift message.', true);
      }
      window.location.assign('/checkout');
    }

    saveGiftNoteBeacon() {
      if (!this.giftNoteIsDirty() || typeof navigator.sendBeacon !== 'function') return;
      const note = this.giftSection()?.querySelector('[data-cart-gift-message]')?.value || '';
      navigator.sendBeacon(
        `${window.Shopify?.routes?.root || '/'}cart/update.js`,
        new Blob([JSON.stringify({ note })], { type: 'application/json' }),
      );
    }

    syncGiftOptions(snapshot) {
      const section = this.giftSection();
      if (!section) return;
      const toggle = section.querySelector('[data-cart-gift-toggle]');
      const textarea = section.querySelector('[data-cart-gift-message]');
      const wrapper = section.querySelector('[data-cart-gift-message-wrap]');
      const enabled = this.giftLines(snapshot.cart).length > 0 || snapshot.cart.attributes?.gift === 'true';
      if (!this.giftBusy && toggle) toggle.checked = enabled;
      if (wrapper) wrapper.hidden = !toggle?.checked;
      if (!this.giftBusy && textarea && document.activeElement !== textarea) {
        textarea.value = String(snapshot.cart.note || '');
      }
    }

    scheduleLocationAlert() {
      const alert = this.querySelector('[data-cart-location-alert]');
      const region = alert?.dataset.locationRegion?.trim();
      if (!alert || !region || this.locationAlertTimer || this.locationRegionChecked === region) return;
      this.locationAlertTimer = window.setTimeout(async () => {
        this.locationAlertTimer = null;
        this.locationRegionChecked = region;
        try {
          const response = await fetch('https://ipinfo.io/json');
          if (!response.ok) return;
          const location = await response.json();
          if (String(location?.region || '').trim().toLowerCase() === region.toLowerCase()) {
            this.locationAlertVisible = true;
            const current = this.querySelector('[data-cart-location-alert]');
            if (current) current.hidden = false;
          }
        } catch { /* The cart stays usable when the optional location service is unavailable. */ }
      }, 2000);
    }

    addProduct(button) {
      const variantId = Number(button?.dataset.variantId || 0);
      if (!variantId || button.disabled) return;
      const restoreThresholdButton = button.hasAttribute('data-cart-threshold-add');
      if (!button.dataset.textIdle) button.dataset.textIdle = button.textContent.trim();
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      const item = { id: variantId, quantity: 1 };
      const price = Number(button.dataset.variantPrice);
      if (button.dataset.variantPrice !== undefined && Number.isFinite(price) && price >= 0) {
        item.price = price;
      }
      const presentation = optimisticLinePresentation(button, item);
      if (presentation) item.__presentation = presentation;
      cart.add([item], { context: 'cart', contextEl: this })
        .catch((error) => {
          announce(this.liveRegion, error?.message, true);
        })
        /* The ring holds for the add, not for the microtask that queued it. */
        .finally(() => {
          button.removeAttribute('aria-busy');
          if (restoreThresholdButton) button.disabled = false;
        });
      this.scrollToTop();
      if (!restoreThresholdButton) this.render(cart.get());
    }

    setExpanded(expanded) {
      for (const trigger of cartTriggers()) {
        trigger.setAttribute('aria-expanded', String(expanded));
      }
    }

    morphContent(html) {
      const content = this.querySelector('[data-cart-drawer-content]');
      if (!content) return;
      const template = document.createElement('template');
      template.innerHTML = html;
      const next = template.content.querySelector('[data-cart-drawer-content]');
      if (!next) return;
      if (this.isOpen) eagerLoadCartItemImages(next);
      const items = content.querySelector('cart-items-component');
      const apply = () => this.applyContentMorph(content, next);
      if (items?.hasActiveCartMotion?.()) {
        this.heldContentMorph = apply;
        this.syncCheckout();
        return;
      }
      apply();
    }

    flushHeldContentMorph() {
      if (!this.heldContentMorph) return;
      const items = this.querySelector('cart-items-component');
      if (items?.hasActiveCartMotion?.()) return;
      const apply = this.heldContentMorph;
      this.heldContentMorph = null;
      apply();
    }

    applyContentMorph(content, next) {
      const hydrating = content.hasAttribute('data-cart-drawer-pending');
      const swapRegions = [
        '[data-filled-state]',
        '[data-empty-state]',
        '[data-cart-footer]',
        '[data-milestones]',
        '[data-empty-ticker]',
        '[data-cart-banners]',
      ];
      const geometry = content.querySelector('cart-items-component')?.captureRowGeometry?.() ?? null;
      const clientHidden = swapRegions.map((selector) => {
        const el = content.querySelector(selector);
        return el ? el.hidden : null;
      });
      const giftDraft = (() => {
        const section = content.querySelector('[data-cart-gift-section]');
        const textarea = section?.querySelector('[data-cart-gift-message]');
        const toggle = section?.querySelector('[data-cart-gift-toggle]');
        return section && textarea && toggle
          ? { value: textarea.value, checked: toggle.checked, focused: document.activeElement === textarea }
          : null;
      })();
      // Every region in the motion table is revealed and retired by a client
      // module, never by the section, so the server's `hidden` is stale markup
      // here — restated it would set the whole drawer moving on every morph.
      const clientRegions = [...content.querySelectorAll(CART_CLIENT_REGION_SELECTOR)]
        .map((el) => [el, el.hidden]);
      const nextRows = new Map(
        Array.from(next.querySelectorAll('[data-cart-item][data-key]'))
          .map((row) => [row.dataset.key, row]),
      );
      const retainedUpsells = [...content.querySelectorAll('[data-cart-upsells-region]')]
        .filter((region) => next.querySelector(`[data-cart-upsells-region="${region.dataset.cartUpsellsRegion}"]`));
      const retainedMilestoneSegments = content.querySelector(
        '[data-milestones] .yise-cart__progress-tracker-segments',
      );
      const nextMilestoneSegments = next.querySelector(
        '[data-milestones] .yise-cart__progress-tracker-segments',
      );
      const retainedMedia = [];
      for (const row of content.querySelectorAll('[data-cart-item][data-key], [data-cart-optimistic-line][data-key]')) {
        const nextRow = nextRows.get(row.dataset.key);
        const media = row.querySelector('.yise-cart__flyout-item-media');
        const nextMedia = nextRow?.querySelector('.yise-cart__flyout-item-media');
        const image = media?.querySelector('img');
        const nextImage = nextMedia?.querySelector('img');
        if (!media || !image || imageSourceKey(image) !== imageSourceKey(nextImage)) continue;
        media.setAttribute('data-skip-node-update', '');
        retainedMedia.push({ media, image, nextImage });
      }
      for (const region of retainedUpsells) region.setAttribute('data-skip-node-update', '');
      if (retainedMilestoneSegments && nextMilestoneSegments) {
        retainedMilestoneSegments.setAttribute('data-skip-node-update', '');
      }
      try {
        morph(content, next);
      } finally {
        for (const region of retainedUpsells) region.removeAttribute('data-skip-node-update');
        retainedMilestoneSegments?.removeAttribute('data-skip-node-update');
        for (const { media, image, nextImage } of retainedMedia) {
          media.removeAttribute('data-skip-node-update');
          media.removeAttribute('data-cart-optimistic-media');
          for (const name of ['alt', 'width', 'height', 'sizes', 'loading', 'decoding', 'fetchpriority']) {
            if (nextImage.hasAttribute(name)) image.setAttribute(name, nextImage.getAttribute(name));
            else image.removeAttribute(name);
          }
        }
      }
      swapRegions.forEach((selector, i) => {
        const el = content.querySelector(selector);
        if (el && clientHidden[i] !== null) el.hidden = clientHidden[i];
      });
      for (const [el, hidden] of clientRegions) {
        if (el.isConnected && el.hidden !== hidden) el.hidden = hidden;
      }
      if (giftDraft) {
        const section = content.querySelector('[data-cart-gift-section]');
        const textarea = section?.querySelector('[data-cart-gift-message]');
        const toggle = section?.querySelector('[data-cart-gift-toggle]');
        if (textarea) textarea.value = giftDraft.value;
        if (toggle) toggle.checked = giftDraft.checked;
        const wrapper = section?.querySelector('[data-cart-gift-message-wrap]');
        if (wrapper) wrapper.hidden = !giftDraft.checked;
        if (giftDraft.focused) focusWhenVisible(textarea, { preventScroll: true });
      }
      if (this.locationAlertVisible) {
        const alert = content.querySelector('[data-cart-location-alert]');
        if (alert) alert.hidden = false;
      } else {
        this.scheduleLocationAlert();
      }
      const snapshot = cart.get();
      this.render(snapshot);
      const cartItems = content.querySelector('cart-items-component');
      if (cartItems && typeof cartItems.syncRegionInteractivity === 'function') {
        cartItems.syncRegionInteractivity();
      }
      cartItems?.applyRowGeometry?.(geometry);
      this.observeScrollGeometry();
      const discount = this.querySelector('cart-discount-component');
      if (discount && typeof discount.render === 'function') discount.render(snapshot);
      if (hydrating) this.revealHydratedContent(content);
      this.dispatchEvent(new CustomEvent('yise:cart:morphed', { bubbles: true }));
    }

    dispatchViewEvent() {
      const itemsEl = this.querySelector('cart-items-component[view-event-payload]');
      const SE = window.__yiseStandardEvents;
      if (!itemsEl || !SE || typeof SE.CartViewEvent !== 'function') return;
      try {
        const payload = JSON.parse(itemsEl.getAttribute('view-event-payload'));
        itemsEl.dispatchEvent(new SE.CartViewEvent({ ...payload, context: 'dialog' }));
      } catch { /* payload malformed — never break the drawer */ }
    }

    render(snapshot) {
      this.toggleAttribute('data-cart-pending', snapshot.pending);
      this.toggleAttribute('data-cart-empty', snapshot.cart.item_count === 0);
      updateTriggerCounts(snapshot.cart.item_count);
      this.syncCheckout(snapshot);
      this.syncGiftOptions(snapshot);
      const inCart = new Set(snapshot.cart.items.map((l) => Number(l.id)));
      for (const button of this.querySelectorAll('[data-add-bundle], [data-add-recommendation]')) {
        if (button.hasAttribute('aria-busy')) continue;
        const variantId = Number(button.dataset.variantId || 0);
        if (!variantId) continue;
        if (inCart.has(variantId)) {
          if (!button.dataset.textIdle) {
            const label = button.querySelector('[data-button-text]') || button;
            button.dataset.textIdle = label.textContent.trim();
          }
          button.disabled = true;
          button.dataset.addState = 'added';
          setAddButtonLabel(button, button.dataset.textAdded);
        } else if (button.disabled) {
          button.disabled = false;
          delete button.dataset.addState;
          setAddButtonLabel(button, button.dataset.textIdle);
        }
      }
      this.scheduleScrollStateUpdate();
    }

    /**
     * The checkout control cannot truthfully submit while its server-rendered line
     * inputs lag an optimistic row or an in-flight keyed morph. YISE's checkout is
     * an `<a href="/checkout">` (Narvar + no-JS fallback), so the held state is an
     * aria/class pair rather than `disabled`.
     */
    syncCheckout(snapshot = cart.get()) {
      const checkout = this.querySelector('[data-checkout]');
      if (!(checkout instanceof HTMLElement)) return;
      const appCheckout = this.querySelector('narvar-shipping-protection-buttons')
        ?.shadowRoot?.querySelector('button[part~="button-checkout"]');
      let focused = document.activeElement;
      while (focused?.shadowRoot?.activeElement) focused = focused.shadowRoot.activeElement;
      const checkoutFocused = focused === checkout || focused === appCheckout;
      const items = this.querySelector('cart-items-component');
      const visualWorkPending = typeof items?.hasPendingCartVisuals === 'function'
        ? items.hasPendingCartVisuals()
        : Boolean(this.querySelector('[data-cart-optimistic-line], [data-removing]'));
      const empty = snapshot.cart.item_count === 0;
      const lineWorkPending = snapshot.pending
        && snapshot.pendingOps.some((op) => op.type !== 'discount');
      const held = !empty
        && (lineWorkPending || visualWorkPending || Boolean(this.heldContentMorph));
      const blocked = empty || held;
      if (checkout instanceof HTMLButtonElement) checkout.disabled = blocked;
      checkout.setAttribute('aria-disabled', blocked ? 'true' : 'false');
      checkout.classList.toggle('is-disabled', blocked);
      checkout.toggleAttribute('data-cart-held', held);
      if (held) checkout.setAttribute('aria-busy', 'true');
      else checkout.removeAttribute('aria-busy');
      if (checkoutFocused && !empty) {
        const target = held || !appCheckout ? checkout : appCheckout;
        if (focused !== target) target.focus({ preventScroll: true });
      }
    }
  }

  class CartItemsComponent extends Component {
    ready() {
      this.wantEmptyFocus = false;
      this.emptyTransitionRequested = false;
      this.removals = new Map();
      this.resyncedGeneration = null;
      this.optimisticIdentities = new WeakMap();
      this.pendingOptimisticRows = new Set();
      this.lineIntents = new Map();
      this.knownKeys = new Set(
        Array.from(this.querySelectorAll('[data-key]')).map((row) => row.dataset.key),
      );
      const list = this.querySelector('[data-cart-list]') || this;
      this.observer = new MutationObserver((records) => this.handleDomMutations(records));
      this.observer.observe(list, { childList: true });

      this.listen(this, 'change', (event) => this.handleSellingPlanChange(event));

      /* `#cart-form` posts keyed quantity updates on the no-JavaScript path and its only submit
         button lives in <noscript>. A form with no submit button still submits implicitly when it
         holds a single field, so Enter in the quantity input of a one-line cart would navigate the
         shopper to /cart. The engine owns the update; the typed value is already committed by the
         input's own `change`. */
      this.listen(this, 'submit', (event) => {
        if (event.target instanceof HTMLFormElement) event.preventDefault();
      });

      this.unsubscribe?.();
      this.unsubscribe = cart.subscribe((snapshot) => this.render(snapshot));
      this.closest('cart-drawer')?.flushHeldContentMorph?.();
    }

    teardown() {
      this.observer?.disconnect();
      this.observer = null;
      this.unsubscribe?.();
      this.unsubscribe = null;
      for (const removal of this.removals.values()) removal.controller.dispose();
      this.removals.clear();
      this.pendingOptimisticRows.clear();
      this.lineIntents.clear();
    }

    get contentRoot() { return this.closest('[data-cart-drawer-content]'); }

    get drawerPanel() { return this.closest('cart-drawer')?.panel ?? null; }

    get liveRegion() { return cartLiveRegion(this); }

    /**
     * The action a control asked for, recorded at click time. The engine's
     * pending queue names a line but not the gesture behind it, and the drawer's
     * loader vocabulary distinguishes all four.
     */
    noteLineIntent(key, action) {
      if (!key || !action) return;
      this.lineIntents.set(key, action);
    }

    /**
     * Rebuild the three busy signals from one engine snapshot:
     * `_pending` (an optimistic row), `_pendingLoading` (a settled row with work
     * queued against it) and `loadingLineItems[key]`, whose value is the action
     * name 'increase' | 'decrease' | 'subscription' | 'remove'.
     */
    lineBusyMap(snapshot) {
      const settledByKey = new Map(
        (snapshot.authoritative?.items || []).map((line) => [line.key, line]),
      );
      const map = new Map();
      const live = new Set();
      for (const op of snapshot.pendingOps) {
        if (op.type !== 'change' || !op.key) continue;
        live.add(op.key);
        const intent = this.lineIntents.get(op.key);
        if (intent) {
          map.set(op.key, intent);
          continue;
        }
        if (op.quantity === 0) {
          map.set(op.key, 'remove');
          continue;
        }
        const settled = settledByKey.get(op.key);
        if (typeof op.quantity !== 'number' || !settled) {
          map.set(op.key, true);
          continue;
        }
        if (op.quantity > settled.quantity) map.set(op.key, 'increase');
        else if (op.quantity < settled.quantity) map.set(op.key, 'decrease');
        else map.set(op.key, true);
      }
      for (const key of this.removals.keys()) {
        map.set(key, 'remove');
        live.add(key);
      }
      for (const key of [...this.lineIntents.keys()]) {
        if (!live.has(key)) this.lineIntents.delete(key);
      }
      return map;
    }

    /** Paint one row's busy affordances from the map above. */
    applyLineBusyState(row, line, busyMap) {
      const optimistic = Boolean(line?.__optimistic_line);
      const action = busyMap.get(row.dataset.key) ?? null;
      const busy = optimistic || Boolean(action);

      if (optimistic) row.setAttribute('aria-busy', 'true');
      else row.removeAttribute('aria-busy');
      if (typeof action === 'string') row.dataset.lineAction = action;
      else delete row.dataset.lineAction;
      row.toggleAttribute('data-line-busy', busy);

      setControlBusy(row.querySelector('[data-remove-item]'), busy);

      for (const button of row.querySelectorAll('.yise-cart__flyout-item-quantity-widget-button')) {
        setControlBusy(button, busy);
      }

      const numberInput = row.querySelector('input[data-cart-quantity-field]');
      if (numberInput && numberInput.dataset.disabledByVariant !== 'true') {
        numberInput.readOnly = busy;
        if (busy) numberInput.setAttribute('aria-busy', 'true');
        else numberInput.removeAttribute('aria-busy');
      }

      // The subscription select uses the narrower pair: it is absent from an
      // optimistic row entirely, so only queued work against a settled line holds it.
      const subscriptionBusy = Boolean(action);
      setControlBusy(
        row.querySelector('.yise-cart__subscription-dropdown-wrapper select, [data-line-selling-plan]'),
        subscriptionBusy,
      );
    }

    renderLinePrice(row, line, freeLabel = '') {
      const hasFinalPrice = typeof line.final_line_price === 'number';
      const authoritativeOriginalLinePrice = finiteNumber(line.original_line_price);
      const sellingPlanCompareAtPrice = finiteNumber(
        line.selling_plan_allocation?.compare_at_price,
      );
      const sellingPlanOriginalLinePrice = sellingPlanCompareAtPrice === null
        ? null
        : sellingPlanCompareAtPrice * Math.max(1, finiteNumber(line.quantity) || 1);
      const originalLinePrice = Math.max(
        authoritativeOriginalLinePrice || 0,
        sellingPlanOriginalLinePrice || 0,
      );
      const hasOriginalPrice = authoritativeOriginalLinePrice !== null
        || sellingPlanOriginalLinePrice !== null;
      const onSale = hasFinalPrice
        && hasOriginalPrice
        && originalLinePrice > line.final_line_price;

      if (hasFinalPrice) {
        row.dataset.lineTotal = String(line.final_line_price);
        const finalPrice = row.querySelector('[data-line-final-price]');
        const formattedFinalPrice = line.final_line_price === 0
          ? freeLabel || money(0)
          : money(line.final_line_price);
        if (finalPrice && finalPrice.textContent.trim() !== formattedFinalPrice) {
          finalPrice.textContent = formattedFinalPrice;
        }
      }

      const originalPrice = row.querySelector('[data-line-original-price]');
      if (hasOriginalPrice && originalPrice) {
        const formattedOriginalPrice = money(originalLinePrice);
        if (originalPrice.textContent.trim() !== formattedOriginalPrice) {
          originalPrice.textContent = formattedOriginalPrice;
        }
      }

      row.querySelector('[data-line-original-price-wrapper]')
        ?.toggleAttribute('hidden', !onSale);
      row.querySelector('[data-line-price-label-regular]')
        ?.toggleAttribute('hidden', onSale);
      row.querySelector('[data-line-price-label-sale]')
        ?.toggleAttribute('hidden', !onSale);
    }

    /**
     * Give an optimistic row the quantity shape its SETTLED row will have.
     *
     * The unused branch is REMOVED, not hidden: the stepper sets an author
     * `display`, which outranks the UA `[hidden]` rule.
     */
    renderLineQuantityShape(row, line) {
      const quantityControl = row.querySelector('[data-cart-optimistic-quantity-control]');
      const stepper = row.querySelector('[data-cart-optimistic-stepper]');
      const freeBranch = row.querySelectorAll('[data-cart-optimistic-free-quantity]');
      if (!stepper && !freeBranch.length) return;
      if (finiteNumber(line.final_line_price) !== 0) {
        for (const node of freeBranch) node.remove();
        return;
      }
      row.dataset.itemType = 'gift';
      if (quantityControl) quantityControl.remove();
      else stepper?.remove();
      for (const node of freeBranch) node.removeAttribute('hidden');
    }

    /**
     * Give an optimistic gift or sample row the badge its settled row shows in
     * place of a price, resolved the way the section resolves it: a sample's
     * `_promoTitle`, a gift's offer badge, then the fixed copy.
     */
    renderLineBadge(row, line) {
      const badge = row.querySelector('[data-cart-optimistic-badge]');
      if (!badge) return;
      const properties = line.properties || {};
      const sample = properties['_gwp-free-sample'] === 'true';
      if (!sample && !isGiftLine(line)) {
        badge.remove();
        return;
      }
      badge.textContent = sample
        ? properties._promoTitle || 'Free Sample'
        : line.__presentation?.badge || 'Free Gift!';
      for (const node of [...badge.parentElement.children]) {
        if (node !== badge) node.remove();
      }
    }

    /** Give an optimistic gift row the PLACEMENT its settled row will have. */
    renderLineGiftPosition(row, line) {
      if (!isGiftLine(line)) return;
      row.dataset.gwpPosition = line.properties._gwp_display_position === 'first' ? 'first' : 'last';
    }

    createOptimisticRow(line, template) {
      const row = template.content.querySelector('[data-cart-optimistic-line]')?.cloneNode(true);
      const presentation = line.__presentation;
      if (!(row instanceof HTMLElement) || !presentation?.title) return null;

      row.dataset.key = line.key;
      row.dataset.itemId = line.key;
      row.dataset.variantId = String(line.id);
      row.dataset.cartOptimisticMotion = 'pending';
      row.__cartOptimisticIdentity = line;
      if (typeof line.final_price === 'number') {
        row.dataset.unitPrice = String(line.final_price / 100);
      }
      this.renderLinePrice(row, line, template.dataset.cartFreeLabel);
      this.renderLineBadge(row, line);
      this.renderLineQuantityShape(row, line);
      this.renderLineGiftPosition(row, line);
      /* A preview row stands in for lines the cart does not hold yet, so its key names
         nothing the AJAX API would accept. The stepper comes off rather than let a click
         in that window enqueue a change against a key that can never resolve. */
      if (line.__preview_line) {
        const quantityControl = row.querySelector('[data-cart-optimistic-quantity-control]');
        if (quantityControl) quantityControl.remove();
        else row.querySelector('[data-cart-optimistic-stepper]')?.remove();
      }

      const title = row.querySelector('[data-cart-optimistic-title]');
      if (title) {
        title.textContent = presentation.title;
        if (presentation.url) title.setAttribute('href', presentation.url);
      }
      const subtitle = row.querySelector('[data-cart-optimistic-subtitle]');
      if (subtitle) {
        subtitle.textContent = presentation.subtitle || '';
        subtitle.hidden = !presentation.subtitle;
      }
      const variantTitle = row.querySelector('[data-cart-optimistic-variant-title]');
      if (variantTitle) {
        variantTitle.textContent = presentation.variantTitle || '';
        variantTitle.hidden = !presentation.variantTitle;
      }
      const subscription = row.querySelector('[data-cart-optimistic-subscription]');
      if (subscription) {
        if (!presentation.sellingPlanName) {
          subscription.remove();
        } else {
          subscription.hidden = false;
          subscription.removeAttribute('aria-hidden');
          const planName = subscription.querySelector('[data-cart-optimistic-selling-plan]');
          if (planName) planName.textContent = presentation.sellingPlanName;
          const saveLabel = subscription.querySelector('[data-cart-optimistic-selling-plan-save]');
          if (saveLabel) {
            saveLabel.textContent = presentation.sellingPlanSaveLabel || '';
            saveLabel.hidden = !presentation.sellingPlanSaveLabel;
          }
        }
      }
      const quantity = row.querySelector('[data-cart-optimistic-quantity]');
      if (quantity instanceof HTMLInputElement) {
        const value = String(line.quantity);
        quantity.value = value;
        quantity.setAttribute('value', value);
        quantity.dataset.cartLine = line.key;
        quantity.dataset.cartQuantity = value;
        const selector = quantity.closest('cart-quantity-selector-component');
        if (selector) selector.dataset.variantId = String(line.id);
      }
      const quantityText = row.querySelector('[data-line-quantity-text]');
      if (quantityText) quantityText.textContent = String(line.quantity);

      const media = row.querySelector('[data-cart-optimistic-media]');
      if (media && presentation.url) {
        media.setAttribute('href', presentation.url);
        media.setAttribute('aria-label', presentation.title);
      }
      if (media && presentation.image) {
        const image = document.createElement('img');
        for (const name of [
          'alt',
          'width',
          'height',
          'sizes',
          'srcset',
          'class',
          'loading',
          'decoding',
          'fetchpriority',
          'src',
        ]) {
          const value = presentation.image[name];
          if (typeof value === 'string') image.setAttribute(name, value);
        }
        media.replaceChildren(image);
      }
      return row;
    }

    ensureOptimisticRows(cartState) {
      const list = this.querySelector('[data-cart-list]');
      const template = this.querySelector('template[data-cart-optimistic-line-template]');
      if (!(list instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;

      const existingRows = Array.from(list.querySelectorAll(':scope > [data-key]'));
      const existingKeys = new Set(existingRows.map((row) => row.dataset.key));
      const optimisticByKey = new Map(
        cartState.items
          .filter((line) => line.__optimistic_line)
          .map((line) => [line.key, line]),
      );
      for (const row of existingRows) {
        if (!row.hasAttribute('data-cart-optimistic-line')) continue;
        const line = optimisticByKey.get(row.dataset.key);
        if (!line) continue;
        this.optimisticIdentities.set(row, line);
        this.knownKeys.add(line.key);
        if (row.dataset.cartOptimisticMotion !== 'pending') continue;
        if (panelIsOpen(this.drawerPanel)) {
          this.startOptimisticExpansion(row);
        } else {
          this.pendingOptimisticRows.add(row);
        }
      }
      const missing = cartState.items.filter(
        (line) => line.__optimistic_line
          && line.__presentation?.title
          && !existingKeys.has(line.key),
      );
      for (const line of [...missing].reverse()) {
        const row = this.createOptimisticRow(line, template);
        if (!row) continue;
        this.optimisticIdentities.set(row, line);
        this.knownKeys.add(line.key);
        this.insertOptimisticRow(list, row);
        this.pendingOptimisticRows.add(row);
        this.startOptimisticExpansion(row);
      }
    }

    /** Hand a just-adopted row its controls back. */
    releaseSettlingControls(row, line) {
      const stepper = row.querySelector('[data-cart-optimistic-stepper]');
      if (!stepper) return;
      for (const control of stepper.querySelectorAll('button[disabled], input[disabled]')) {
        if (control.dataset.disabledByVariant === 'true') continue;
        control.disabled = false;
      }
      const input = stepper.querySelector('[data-cart-optimistic-quantity]');
      if (input) input.dataset.cartLine = line.key;
    }

    /**
     * A cart quantity field's form name IS its line key.
     *
     * Returns the field so the caller can sync its value without a second query.
     */
    bindQuantityFieldToLine(row, line) {
      const field = row.querySelector('input[data-cart-quantity-field]');
      if (!field) return null;
      if (line?.key && !line.__optimistic_line) {
        field.name = `updates[${line.key}]`;
        field.dataset.cartLine = line.key;
      }
      return field;
    }

    /** Put a new optimistic row where its settled row will be. */
    insertOptimisticRow(list, row) {
      const groupOf = (element) => {
        const position = element.dataset?.gwpPosition;
        if (position === 'first') return 0;
        if (position === 'last') return 2;
        return 1;
      };
      const target = groupOf(row);
      const anchor = Array.from(list.children).find(
        (child) => child.matches?.('[data-key]') && groupOf(child) >= target,
      );
      list.insertBefore(row, anchor || null);
    }

    startOptimisticExpansion(row) {
      if (!row?.isConnected || row.dataset.cartOptimisticMotion === 'complete') return;
      const open = panelIsOpen(this.drawerPanel);
      if (open && row.hidden && row.dataset.cartOptimisticMotion === 'pending') {
        row.hidden = false;
      }
      if (!open || row.getClientRects().length === 0) {
        row.dataset.cartOptimisticMotion = 'pending';
        this.pendingOptimisticRows.add(row);
        return;
      }
      this.pendingOptimisticRows.delete(row);
      row.hidden = false;
      row.dataset.cartOptimisticMotion = 'complete';
      this.flushHeldMorph();
      this.syncCheckout();
    }

    startPendingOptimisticExpansions() {
      for (const row of [...this.pendingOptimisticRows]) {
        if (!row.isConnected) {
          this.pendingOptimisticRows.delete(row);
          continue;
        }
        if (row.dataset.cartOptimisticMotion !== 'pending') {
          this.pendingOptimisticRows.delete(row);
          continue;
        }
        this.startOptimisticExpansion(row);
      }
    }

    preparePendingOptimisticRows() {
      for (const row of this.pendingOptimisticRows) {
        if (!row.isConnected) continue;
        row.hidden = false;
        row.dataset.cartOptimisticMotion = 'complete';
      }
      this.pendingOptimisticRows.clear();
    }

    discardScheduledOptimisticRow(row) {
      this.pendingOptimisticRows.delete(row);
      this.optimisticIdentities.delete(row);
      delete row.__cartOptimisticIdentity;
      this.knownKeys.delete(row.dataset.key);
      row.remove();
      this.syncCheckout();
    }

    /**
     * The drawer's scroll offset, read before a settle morph rewrites the list.
     *
     * The morph can replace every row, which resets the scroll region to the
     * top; restoring the offset afterwards keeps the customer where they were.
     */
    captureRowGeometry() {
      if (!panelIsOpen(this.drawerPanel)) return null;
      const scrollRegion = this.closest('cart-drawer')?.scrollRegion ?? null;
      if (!scrollRegion) return null;
      return { scrollRegion, scrollTop: scrollRegion.scrollTop };
    }

    applyRowGeometry(geometry) {
      if (!geometry) return;
      const { scrollRegion, scrollTop } = geometry;
      if (scrollRegion?.isConnected && scrollTop !== null && scrollRegion.scrollTop !== scrollTop) {
        scrollRegion.scrollTop = scrollTop;
      }
    }

    handleDomMutations(records) {
      const added = new Map();
      const removed = new Set();
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          const key = node.getAttribute?.('data-key');
          if (key) added.set(key, node);
        }
        for (const node of record.removedNodes) {
          if (node.nodeType !== 1) continue;
          const key = node.getAttribute?.('data-key');
          if (key) removed.add(key);
        }
      }
      for (const key of removed) {
        if (!added.has(key)) this.knownKeys.delete(key);
      }
      for (const key of added.keys()) this.knownKeys.add(key);
    }

    onLineItemRemove(indexOneBased, event) {
      event?.preventDefault?.();
      const row = event?.target?.closest?.('[data-key]');
      if (!row || row.hasAttribute('data-line-busy') || row.dataset.removing) return;
      this.noteLineIntent(row.dataset.key, 'remove');
      this.removeLine(row);
    }

    handleSellingPlanChange(event) {
      const select = event.target?.closest?.(
        '[data-line-selling-plan], .yise-cart__subscription-dropdown-wrapper select',
      );
      if (!(select instanceof HTMLSelectElement)) return;
      const row = select.closest('[data-key]');
      const key = select.dataset.lineKey || row?.dataset.key;
      if (!key) return;
      if (row?.hasAttribute('data-line-busy')) return;
      const sellingPlan = select.value ? Number(select.value) : null;
      this.noteLineIntent(key, 'subscription');
      cart.changeLine(key, { selling_plan: sellingPlan }, { context: 'cart', contextEl: this })
        .catch((error) => this.showLineError(key, error?.message));
    }

    removeLine(row) {
      const key = row?.dataset.key;
      if (!key) return;
      const visibleRows = Array.from(this.querySelectorAll('[data-key]'))
        .filter((candidate) => !candidate.hidden && !candidate.dataset.removing);
      const remainingPaidRows = visibleRows.filter((candidate) => candidate !== row && candidate.dataset.itemType === 'paid');
      const adjacent = remainingPaidRows[0];
      const willEmpty = remainingPaidRows.length === 0;
      const title = cartLineTitle(row);

      this.noteLineIntent(key, 'remove');

      if (willEmpty) {
        this.emptyTransitionRequested = true;
        row.inert = true;
        this.wantEmptyFocus = true;
        this.closest('cart-drawer')?.querySelector('[data-cart-close]')?.focus({ preventScroll: true });
      } else if (adjacent) {
        adjacent.querySelector('button:not([disabled]), input:not([disabled]), select:not([disabled])')
          ?.focus({ preventScroll: true });
      }

      cart.changeLine(key, { quantity: 0 }, { context: 'cart', contextEl: this })
        .then(async () => {
          await cart.settled();
          const settledCart = cart.get().cart;
          if (settledCart.items.some((line) => line.key === key)) return;
          announce(
            this.closest('cart-drawer')?.liveRegion,
            settledCart.item_count === 0 ? 'Your bag is empty.' : `Removed ${title} from bag.`,
          );
        })
        .catch((error) => {
          if (willEmpty) {
            this.emptyTransitionRequested = false;
            row.inert = false;
            row.removeAttribute('data-line-busy');
            this.render(cart.get());
            this.flushHeldMorph();
          }
          this.showLineError(key, error?.message);
        });
    }

    render(snapshot) {
      const cartState = snapshot.cart;
      const skipRemovalMotion = this.emptyTransitionRequested || cartState.item_count === 0;
      this.ensureOptimisticRows(cartState);
      const projectedByKey = new Map(cartState.items.map((line) => [line.key, line]));
      const busyMap = this.lineBusyMap(snapshot);

      const domRows = Array.from(this.querySelectorAll('[data-key]'));
      const domKeys = new Set(domRows.map((row) => row.dataset.key));
      const unclaimed = cartState.items.filter((line) => !domKeys.has(line.key));
      for (const row of domRows) {
        let line = projectedByKey.get(row.dataset.key);
        const optimisticIdentity = this.optimisticIdentities.get(row)
          || row.__cartOptimisticIdentity;
        if (!line && (optimisticIdentity || !row.hidden)) {
          const variantPrefix = `${row.dataset.key.split(':')[0]}:`;
          const adopted = optimisticIdentity
            ? unclaimed.find((candidate) => linesShareIdentity(candidate, optimisticIdentity))
            : unclaimed.find((candidate) => String(candidate.key).startsWith(variantPrefix));
          if (adopted) {
            unclaimed.splice(unclaimed.indexOf(adopted), 1);
            this.knownKeys.delete(row.dataset.key);
            this.knownKeys.add(adopted.key);
            row.dataset.key = adopted.key;
            row.dataset.itemId = adopted.key;
            this.pendingOptimisticRows.delete(row);
            this.optimisticIdentities.delete(row);
            delete row.__cartOptimisticIdentity;
            row.removeAttribute('data-cart-optimistic-line');
            row.querySelector('[data-cart-optimistic-loader]')?.remove();
            line = adopted;
            this.releaseSettlingControls(row, adopted);
          }
        }
        if (!line) {
          if (row.hasAttribute('data-cart-optimistic-line')
            && row.dataset.cartOptimisticMotion === 'scheduled') {
            this.discardScheduledOptimisticRow(row);
            continue;
          }
          if (!row.hidden && !row.dataset.removing) {
            if (skipRemovalMotion) {
              row.inert = true;
              row.setAttribute('data-line-busy', '');
              continue;
            }
            row.dataset.removing = 'true';
            this.startRemoval(row);
          }
          continue;
        }
        if (row.dataset.removing) this.restoreRemoval(row);
        if (row.dataset.cartOptimisticMotion !== 'scheduled') row.hidden = false;
        const qtyInput = this.bindQuantityFieldToLine(row, line);
        if (qtyInput && document.activeElement !== qtyInput && Number(qtyInput.value) !== line.quantity) {
          qtyInput.value = line.quantity;
        }
        const quantityText = row.querySelector('[data-line-quantity-text]');
        if (quantityText && quantityText.textContent.trim() !== String(line.quantity)) {
          quantityText.textContent = String(line.quantity);
        }
        const freeLabel = this.querySelector('template[data-cart-optimistic-line-template]')
          ?.dataset.cartFreeLabel;
        this.renderLinePrice(row, line, freeLabel);
        this.applyLineBusyState(row, line, busyMap);
      }
      if (!snapshot.pending) {
        for (const [key, removal] of this.removals) {
          if (!projectedByKey.has(key)) {
            removal.confirmed = true;
            this.finishRemovalWhenReady(key);
          }
        }
      }

      this.syncStates(snapshot);
      /* Milestones track server truth: see the note on `buildView` in assets/cart-gwp.js.
         Totals stay on the projection, because a line's money is the line. */
      this.renderMilestone(snapshot.authoritative || cartState);
      this.renderTotals(cartState);
      this.requestMarkupForUnrenderableLines(snapshot);
      this.syncCheckout();
    }

    /** Settled lines the rendered markup cannot show. */
    hasUnrenderableLines(snapshot) {
      const list = this.querySelector('[data-cart-list]');
      if (!list) return false;
      const rendered = new Set(
        Array.from(list.querySelectorAll(':scope > [data-key]')).map((row) => row.dataset.key),
      );
      return snapshot.cart.items.some(
        (line) => !line.__optimistic_line && !rendered.has(line.key),
      );
    }

    /** Ask the server to re-render the drawer when its rows can no longer describe the cart. */
    requestMarkupForUnrenderableLines(snapshot) {
      if (snapshot.pending) return;
      if (this.resyncedGeneration === snapshot.generation) return;
      if (!this.hasUnrenderableLines(snapshot)) return;
      this.resyncedGeneration = snapshot.generation;
      cart.resyncSections().then((delivered) => {
        if (!delivered && this.resyncedGeneration === snapshot.generation) {
          this.resyncedGeneration = null;
        }
      });
    }

    startRemoval(row) {
      const key = row.dataset.key;
      if (!key || this.removals.has(key)) return;
      this.pendingOptimisticRows.delete(row);
      row.inert = true;
      const removal = {
        row,
        controller: null,
        collapsed: false,
        confirmed: false,
      };
      removal.controller = collapseItem(row, (state) => {
        if (state === 'collapsed') {
          removal.collapsed = true;
          this.finishRemovalWhenReady(key);
        } else {
          delete row.dataset.removing;
          this.removals.delete(key);
          this.render(cart.get());
          this.flushHeldMorph();
          this.syncCheckout();
        }
      });
      this.removals.set(key, removal);
    }

    restoreRemoval(row) {
      const removal = Array.from(this.removals.values()).find((entry) => entry.row === row);
      if (!removal) return;
      removal.confirmed = false;
      row.inert = false;
      removal.controller.restore();
    }

    finishRemovalWhenReady(key) {
      const removal = this.removals.get(key);
      if (!removal?.collapsed) return;
      if (!removal.confirmed) {
        this.syncStates(cart.get());
        return;
      }
      removal.controller.dispose();
      delete removal.row.dataset.removing;
      const wasOptimistic = removal.row.hasAttribute('data-cart-optimistic-line');
      if (wasOptimistic) {
        this.pendingOptimisticRows.delete(removal.row);
        this.optimisticIdentities.delete(removal.row);
        delete removal.row.__cartOptimisticIdentity;
      }
      const trailingWhitespace = removal.row.nextSibling;
      removal.row.remove();
      if (trailingWhitespace?.nodeType === Node.TEXT_NODE
        && trailingWhitespace.textContent.trim() === '') {
        trailingWhitespace.remove();
      }
      this.knownKeys.delete(key);
      this.removals.delete(key);
      this.lineIntents.delete(key);
      this.syncStates(cart.get());
      this.flushHeldMorph();
      this.syncCheckout();
    }

    hasPendingCartVisuals() {
      return Boolean(
        this.pendingOptimisticRows?.size
        || this.removals?.size
        || this.emptyTransitionRequested
        || this.querySelector('[data-cart-optimistic-line], [data-removing]')
      );
    }

    hasActiveCartMotion() {
      return Boolean(
        this.removals?.size
        || this.emptyTransitionRequested
      );
    }

    syncCheckout() {
      this.closest('cart-drawer')?.syncCheckout?.(cart.get());
    }

    flushHeldMorph() {
      if (this.removals.size
        || this.emptyTransitionRequested) return;
      this.closest('cart-drawer')?.flushHeldContentMorph?.();
    }

    syncStates(snapshot) {
      const hasItems = cartVisuallyHasItems(snapshot.cart, this.emptyTransitionRequested);
      const root = this.contentRoot;
      if (!root) return;
      for (const removal of this.removals.values()) {
        if (!removal.collapsed) return;
      }
      if (this.emptyTransitionRequested && snapshot.cart.item_count === 0 && !snapshot.pending) {
        this.emptyTransitionRequested = false;
        queueMicrotask(() => this.flushHeldMorph());
      }
      this.applyStateVisibility(hasItems);
    }

    syncRegionInteractivity() {
      const root = this.contentRoot;
      if (!root) return;
      for (const selector of [
        '[data-filled-state]',
        '[data-empty-state]',
        '[data-cart-footer]',
        '[data-milestones]',
        '[data-empty-ticker]',
        '[data-cart-banners]',
      ]) {
        const element = root.querySelector(selector);
        if (element) element.inert = element.hidden;
      }
    }

    /** The header, scroll region and title carry YISE's own empty-state classes. */
    applyShellState(root, hasItems) {
      root.querySelector('.yise-cart__flyout-header')?.classList.toggle('is-empty', !hasItems);
      const content = root.querySelector('.yise-cart__flyout-content');
      if (content) {
        content.classList.toggle('has-items', hasItems);
        content.classList.toggle('no-items', !hasItems);
      }
      const emptyTitle = root.querySelector('[data-cart-empty-title]');
      if (emptyTitle) emptyTitle.hidden = hasItems;
    }

    applyStateVisibility(hasItems) {
      const root = this.contentRoot;
      if (!root) return;
      const set = (selector, hidden) => {
        const el = root.querySelector(selector);
        if (!el) return;
        el.hidden = hidden;
      };
      set('[data-filled-state]', !hasItems);
      set('[data-empty-state]', hasItems);
      set('[data-cart-footer]', !hasItems);
      set('[data-milestones]', !hasItems);
      set('[data-empty-ticker]', hasItems);
      set('[data-cart-banners]', !hasItems);
      this.applyShellState(root, hasItems);
      this.syncRegionInteractivity();
      if (hasItems && panelIsOpen(this.drawerPanel)) this.startPendingOptimisticExpansions();
      if (!hasItems && this.wantEmptyFocus) {
        this.wantEmptyFocus = false;
        const emptyEl = root.querySelector('[data-empty-state]');
        focusWhenVisible(
          emptyEl?.querySelector('a[href], button:not([disabled]), select:not([disabled])'),
        );
      }
    }

    /**
     * The single writer for every bar's width, so each one animates to its new value.
     *
     * A bar's target arrives three ways: computed by the loop below, rendered by the
     * section into `--cart-drawer-progress`, or carried in by a row `assets/cart-gwp.js`
     * just revealed. None of them animate on their own — the settle morph re-inserts
     * these nodes, and a re-inserted element has no before-change style for the CSS
     * transition to travel from. Writing the width the bar last showed with the
     * transition suppressed, then flushing layout, restores that starting point in the
     * current frame; the real value written after it animates.
     *
     * The tiers are consecutive, so their fills are too: a bar only begins once every
     * earlier bar moving in the same pass has finished, which is what makes the second
     * tier read as continuing the first rather than racing it.
     */
    syncMilestoneBars(root) {
      const bars = [...root.querySelectorAll('[data-milestones] [role="progressbar"]')]
        .map((bar) => ({ bar, row: bar.closest('[data-threshold-cents]') }))
        .filter(({ row }) => row && !row.hidden)
        .sort((left, right) => milestoneOrder(left.row) - milestoneOrder(right.row));

      let queued = 0;
      for (const { bar, row } of bars) {
        const percent = milestoneBarPercent(row, bar);
        if (percent === null) continue;
        const key = milestoneRowKey(row);
        const previous = milestoneProgress.get(key);
        milestoneProgress.set(key, percent);
        if (previous === undefined || previous === percent) {
          /* A settle notifies several times. Rewriting an unchanged bar would strip the
             stagger off a fill still waiting its turn, so an unchanged bar is left alone
             unless its width is not the explicit one this method owns. */
          if (bar.style.width !== `${percent}%`) {
            bar.style.removeProperty('--cart-drawer-progress-delay');
            bar.style.width = `${percent}%`;
          }
          continue;
        }
        const transition = bar.style.transition;
        bar.style.transition = 'none';
        bar.style.width = `${previous}%`;
        void bar.offsetWidth;
        bar.style.transition = transition;
        bar.style.setProperty('--cart-drawer-progress-delay', `${queued * milestoneFillMs(bar)}ms`);
        bar.style.width = `${percent}%`;
        queued += 1;
      }
    }

    /** The single writer for milestone geometry and direction-sensitive motion. */
    renderMilestone(cartState) {
      const root = this.contentRoot;
      if (!root) return;
      const shipping = root.querySelector('[data-shipping-status]');
      const rows = [...root.querySelectorAll(
        '.yise-cart__progress-tracker-segments > [data-threshold-cents]',
      )].filter((row) => !row.hidden);
      if (!rows.length) {
        this.syncMilestoneBars(root);
        return;
      }

      const basisSubtotals = new Map();
      let normalized = null;
      /* Every row on one bar has to be measured with one ruler. `cartState.total_price` is not
         that ruler: it counts the theme's own gift and sample lines and every gift card, so a row
         falling back to it read higher than its neighbour and the two segments disagreed about the
         same cart. `exclude_gift_cards` is per offer, so it is read from the row rather than from
         the section default, which is the flag the reconciler actually applied. */
      const subtotalOf = (row) => {
        const basis = row.dataset.thresholdBasis === 'pre' ? 'pre' : 'post';
        const exclude = row.dataset.excludeGiftCards
          ? row.dataset.excludeGiftCards === 'true'
          : giftCardsExcluded();
        const key = `${basis}:${exclude}`;
        if (!basisSubtotals.has(key)) {
          if (!normalized) normalized = normalizeAjaxCart(cartState);
          basisSubtotals.set(
            key,
            subtotalCentsFor(normalized, basis, { excludeGiftCards: exclude }),
          );
        }
        return basisSubtotals.get(key);
      };

      const thresholdOf = (row) => presentmentThresholdOf(row);
      const layout = milestoneStageLayout(
        rows,
        thresholdOf,
        subtotalOf,
      );
      const completion = [];
      for (const { row, position, thresholdCents, progress, flexGrow } of layout) {
        row.style.setProperty('flex-grow', String(flexGrow));
        row.style.setProperty('order', String(position));
        row.dataset.milestoneProgress = String(progress);
        const barTrack = row.querySelector('.yise-cart__progress-tracker-bar');
        barTrack?.classList.toggle('has-progress', progress > 0);
        barTrack?.classList.toggle('no-progress', progress <= 0);
        const bar = row.querySelector('[role="progressbar"]');
        if (bar) {
          bar.style.width = `${progress}%`;
          bar.setAttribute('aria-valuenow', String(Math.round(progress)));
        }

        const subtotalCents = subtotalOf(row);
        /* A GWP row's completion belongs to `cart-gwp.js`, which takes it from the rules engine.
           Re-deriving it from the subtotal here is what let a tier read as unlocked while the cart
           refused the gift — sold out, campaign capped, first-order only, blocked by an applied
           code, or not offered in this market. Section-authored tiers have no gift behind them, so
           for those the subtotal really is the whole question. */
        const complete = row.hasAttribute('data-gwp-milestone') || thresholdCents <= 0
          ? row.dataset.milestoneComplete === 'true'
          : subtotalCents >= thresholdCents
            || row.dataset.milestoneUnlockedWithoutSpend === 'true';
        row.dataset.milestoneComplete = String(complete);
        row.classList.toggle('is-qualified', complete);
        const check = row.querySelector('[data-milestone-check]');
        const detail = row.querySelector('[data-milestone-detail]');
        /* Every tier still to earn states what it costs, so the bar reads as a ladder rather than
           one step. Only an earned tier drops the line: its label and check already say it is done.
           `snippets/cart-drawer-content.liquid` renders the first paint to the same rule. */
        const applyCompletion = () => {
          if (check) check.hidden = !complete;
          if (detail) detail.hidden = complete;
        };
        applyCompletion();
        /* The server can only render the remainder of the cart it was built for, so the copy is
           rewritten here on every cart change. Rows carrying `data-gwp-milestone` are left alone:
           `cart-gwp.js` owns those, and its remainder comes from the rules engine, which counts
           only what that offer counts. A row with no threshold has no remainder to write. */
        if (detail && thresholdCents > 0 && !row.hasAttribute('data-gwp-milestone')) {
          const copy = complete
            ? row.dataset.milestoneCompleteTemplate || ''
            : (row.dataset.milestoneAwayTemplate || '').replace(
              '__amount__',
              money(remainingCentsFor(thresholdCents, subtotalCents)),
            );
          if (detail.textContent !== copy) detail.textContent = copy;
        }
        completion.push(complete);
      }

      this.syncMilestoneBars(root);

      root.querySelector('[data-milestones]')
        ?.classList.toggle('all-qualified', completion.length > 0 && completion.every(Boolean));

      if (shipping && !shipping.hidden) {
        const detail = shipping.querySelector('[data-shipping-detail]');
        /* The loop above has just written every row's completion from its own owner, so the next
           unmet tier is read back from that rather than re-derived — otherwise the shipping copy
           counts a shopper down to a GWP tier the rules engine already settled. */
        const next = layout.find(({ row, thresholdCents }) => (
          thresholdCents > 0 && row.dataset.milestoneComplete !== 'true'
        ));
        if (detail && next) {
          const thresholdCents = next.thresholdCents;
          const subtotalCents = subtotalOf(next.row);
          const template = next.row.dataset.milestoneAwayTemplate || 'Spend __amount__ more';
          shipping.dataset.shippingThresholdCents = String(thresholdCents);
          shipping.dataset.milestoneComplete = 'false';
          detail.textContent = template.replace(
            '__amount__',
            money(remainingCentsFor(thresholdCents, subtotalCents)),
          );
        } else if (detail) {
          const completed = [...layout].reverse().find(({ row }) => row.dataset.milestoneComplete === 'true');
          detail.textContent = completed?.row.dataset.milestoneCompleteTemplate || '';
          shipping.dataset.milestoneComplete = 'true';
        }
      }
    }

    renderTotals(cartState) {
      const root = this.contentRoot;
      if (!root) return;
      const totalCents = cartState.total_price || 0;
      const count = cartState.item_count || 0;

      const countEl = root.querySelector('[data-subtotal-count]');
      if (countEl) {
        const localizedCount = formatItemCount(count, subtotalCountSamples(root), document.documentElement.lang);
        countEl.dataset.count = String(count);
        if (countEl.textContent.trim() !== localizedCount) countEl.textContent = localizedCount;
      }

      const formattedTotal = money(totalCents);
      const subtotalEl = root.querySelector('[data-subtotal]');
      if (subtotalEl) subtotalEl.textContent = formattedTotal;

      const checkoutPrice = root.querySelector('[data-checkout-price]');
      if (checkoutPrice) checkoutPrice.textContent = formattedTotal;

      const originalEl = root.querySelector('[data-subtotal-original]');
      if (originalEl && cartState.items.length
        && cartState.items.every((line) => typeof line.original_line_price === 'number')) {
        const original = cartState.items.reduce((sum, line) => sum + line.original_line_price, 0);
        originalEl.textContent = money(original);
        originalEl.hidden = original <= totalCents;
      }

      const title = root.querySelector('[data-cart-count]');
      if (title) {
        title.setAttribute('data-cart-count', String(cartState.item_count));
        const titleTemplate = title.dataset.cartTitleTemplate;
        if (titleTemplate) title.textContent = titleTemplate.replace('__count__', String(cartState.item_count));
      }
    }

    showLineError(key, message) {
      announce(this.liveRegion, message, true);
    }
  }

  class CartDiscountComponent extends Component {
    /**
     * Expansion is client state on a server-rendered attribute, so it lives on the instance and
     * every path writes the DOM from it rather than reading the DOM back. The section re-renders
     * `aria-expanded` as a Liquid literal on each morph; `render` runs after every one.
     */
    expanded = false;

    /* The toggle is bound through `on:click` (assets/component.js delegates from the document), not
       through a node captured here: a morph can hand this component a different button element, and
       a listener held on the old node would leave the control visible but dead. */
    ready() {
      this.unsubscribe?.();
      this.unsubscribe = cart.subscribe((snapshot) => this.render(snapshot));
    }

    teardown() {
      this.unsubscribe?.();
      this.unsubscribe = null;
      this.releaseChipHold();
    }

    get toggle() { return this.querySelector('[data-promo-toggle]'); }
    get panel() { return this.querySelector('[data-promo-panel]'); }
    get input() { return this.querySelector('[data-promo-input]'); }
    get errorEl() { return this.querySelector('[data-promo-error]'); }
    /** YISE has no summary block; the footer region is the discount row's owner. */
    discountRow() { return this.closest('[data-cart-footer]')?.querySelector('[data-discount-row]'); }

    togglePromo(event) {
      event?.preventDefault?.();
      if (!this.toggle) return;
      this.expanded = !this.expanded;
      this.syncDisclosure();
      if (this.expanded) focusWhenVisible(this.input, { preventScroll: true });
    }

    /**
     * Write the disclosure state onto whichever toggle and panel the component currently owns.
     * `render` also hides the panel whenever a code is on the cart, so without the panel half a
     * drawer that has ever carried one could never reopen.
     */
    syncDisclosure() {
      this.toggle?.setAttribute('aria-expanded', String(this.expanded));
      const panel = this.panel;
      if (panel) panel.hidden = false;
    }

    async applyDiscount(event) {
      event?.preventDefault?.();
      const input = this.input;
      const applyButton = this.querySelector('[data-promo-apply]');
      const code = input?.value.trim();
      if (!code || this.applying || applyButton?.disabled || applyButton?.hasAttribute('aria-busy')) return;
      this.applying = true;
      this.setAttribute('data-skip-node-update', '');
      const errorEl = this.errorEl;
      if (errorEl) errorEl.hidden = true;
      input?.removeAttribute('aria-invalid');
      input?.removeAttribute('aria-describedby');
      if (applyButton) applyButton.disabled = true;
      applyButton?.setAttribute('aria-busy', 'true');
      try {
        const { cart: settled } = await cart.applyDiscount(code);
        const entry = (settled.discount_codes || []).find(
          (item) => item.code.toLowerCase() === code.toLowerCase(),
        );
        if (!entry || entry.applicable === false) {
          await cart.removeDiscount(code);
          this.showPromoError();
        } else if (input) {
          input.value = '';
          this.expanded = false;
          this.toggle?.setAttribute('aria-expanded', 'false');
          this.hidden = true;
          announce(this.closest('cart-drawer')?.querySelector('[data-cart-live]'),
            `Discount ${code} applied. Bag total ${formatMoney(settled.total_price, env.moneyFormat)}.`);
        }
      } catch {
        this.showPromoError();
      } finally {
        this.applying = false;
        this.removeAttribute('data-skip-node-update');
        const currentApplyButton = this.querySelector('[data-promo-apply]');
        currentApplyButton?.removeAttribute('aria-busy');
        if (currentApplyButton) currentApplyButton.disabled = false;
      }
    }

    showPromoError() {
      const errorEl = this.errorEl;
      const input = this.input;
      this.hidden = false;
      if (errorEl) errorEl.hidden = false;
      this.expanded = true;
      this.syncDisclosure();
      if (input) {
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', errorEl?.id || 'CartDrawerPromoError');
        focusWhenVisible(input, { preventScroll: true });
      }
    }

    async removeDiscount(event) {
      event?.preventDefault?.();
      const row = event.target.closest('[data-discount-row]') || this.discountRow();
      const code = row?.querySelector('[data-discount-code]')?.textContent?.trim();
      if (!code) return;
      this.chipLeaving = true;
      const input = this.input;
      if (input) {
        input.value = '';
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
      }
      try {
        await cart.removeDiscount(code);
      } catch (error) {
        this.releaseChipHold();
        this.render(cart.get());
        throw error;
      }
    }

    /** Bring the promo affordance back once the removed code's chip is gone. */
    revealAfterChipExit() {
      this.releaseChipHold();
      this.render(cart.get());
      this.expanded = true;
      this.syncDisclosure();
      focusWhenVisible(this.input, { preventScroll: true });
    }

    releaseChipHold() {
      this.chipLeaving = false;
    }

    render(snapshot) {
      const toggle = this.toggle;
      if (!toggle) return;
      const applied = snapshot.discountCodes || [];
      if (this.chipLeaving) {
        this.hidden = true;
        if (applied.length === 0) this.revealAfterChipExit();
        return;
      }
      this.hidden = applied.length > 0;
      if (applied.length > 0) {
        this.expanded = false;
        toggle.setAttribute('aria-expanded', 'false');
        const panel = this.panel;
        if (panel) panel.hidden = true;
        return;
      }
      this.syncDisclosure();
    }
  }

  defineElement('cart-drawer', CartDrawerComponent);
  defineElement('cart-items-component', CartItemsComponent);
  defineElement('cart-discount-component', CartDiscountComponent);
}
